import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grow,
  Zoom,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ensureLessonFontStyles } from "../../../utils/fontFamilies";
import LessonAuthoringService from "../../../apis/LessonAuthoringService";
import LessonRichTextEditor from "./LessonRichTextEditor";
import ItemService from "../../../apis/ItemService";
import AssignmentAuthoringShell from "../assignments/AssignmentAuthoringShell";
import {
  BLOOMS_TAXONOMY_SKILLS,
  DEFAULT_LESSON_TYPE,
  DEFAULT_SKILL_TYPE,
  LESSON_TYPE_OPTIONS,
  LESSON_TYPE_PLACEHOLDER,
  LESSON_TYPE_SELECT_OPTIONS,
  LESSON_TYPE_VISUALS,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
ensureLessonFontStyles();

const stripHtmlTags = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const sanitizeLessonDescriptionHtml = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = String(value);

  if (typeof document === "undefined") {
    return stringValue;
  }

  const container = document.createElement("div");
  container.innerHTML = stringValue;

  container.querySelectorAll("script, style").forEach((element) => {
    element.remove();
  });

  container.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();

      if (attributeName.startsWith("on")) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (["href", "src", "xlink:href"].includes(attributeName)) {
        if (/^\s*javascript:/i.test(attribute.value)) {
          element.removeAttribute(attribute.name);
        }
      }
    });
  });

  return container.innerHTML;
};

const DEFAULT_CHAPTER_ID = "1";

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    for (const key of ["results", "items", "data", "hits", "records"]) {
      const candidate = value[key];

      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
  }

  return [];
};

const extractIdentifierValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const extracted = extractIdentifierValue(entry);

      if (extracted !== null) {
        return extracted;
      }
    }

    return null;
  }

  if (typeof value === "object") {
    const candidateKeys = [
      "id",
      "Id",
      "ID",
      "lessonId",
      "lesson_id",
      "lessonID",
      "lessonid",
      "moduleId",
      "module_id",
      "moduleID",
      "moduleid",
      "contentId",
      "content_id",
      "contentID",
      "value",
      "Value",
    ];

    for (const key of candidateKeys) {
      const nested = extractIdentifierValue(value[key]);

      if (nested !== null) {
        return nested;
      }
    }
  }

  return null;
};

const extractTextValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const extracted = extractTextValue(entry);

      if (extracted) {
        return extracted;
      }
    }

    return "";
  }

  if (typeof value === "object") {
    const candidateKeys = [
      "title",
      "name",
      "label",
      "text",
      "value",
      "description",
      "summary",
      "snippet",
      "heading",
    ];

    for (const key of candidateKeys) {
      const extracted = extractTextValue(value[key]);

      if (extracted) {
        return extracted;
      }
    }
  }

  return "";
};

const normaliseCourseSearchResults = (data) => {
  const entries = ensureArray(data);

  return entries
    .map((item, index) => {
      const moduleId =
        extractIdentifierValue(item?.moduleId) ??
        extractIdentifierValue(item?.module_id) ??
        extractIdentifierValue(item?.moduleid) ??
        extractIdentifierValue(item?.module);

      const rawModuleTitle =
        extractTextValue(item?.moduleTitle) ||
        extractTextValue(item?.moduleName) ||
        extractTextValue(item?.module) ||
        extractTextValue(item?.module_label);

      const lessonId =
        extractIdentifierValue(item?.lessonId) ??
        extractIdentifierValue(item?.lesson_id) ??
        extractIdentifierValue(item?.lessonid) ??
        extractIdentifierValue(item?.lesson) ??
        extractIdentifierValue(item?.contentId) ??
        extractIdentifierValue(item?.content_id) ??
        extractIdentifierValue(item?.id);

      const rawLessonTitle =
        extractTextValue(item?.lessonTitle) ||
        extractTextValue(item?.lessonName) ||
        extractTextValue(item?.lesson) ||
        extractTextValue(item?.title) ||
        extractTextValue(item?.name) ||
        extractTextValue(item?.contentTitle);

      const rawSnippet =
        extractTextValue(item?.snippet) ||
        extractTextValue(item?.summary) ||
        extractTextValue(item?.description) ||
        extractTextValue(item?.content) ||
        extractTextValue(item?.text) ||
        extractTextValue(item?.value);

      const rawType =
        extractTextValue(item?.contentType) ||
        extractTextValue(item?.content_type) ||
        extractTextValue(item?.type);

      const lessonTitle = stripHtmlTags(rawLessonTitle).trim();
      const moduleTitle = stripHtmlTags(rawModuleTitle).trim();
      const snippet = stripHtmlTags(rawSnippet).trim();
      const type = stripHtmlTags(rawType).trim();

      return {
        key: `${moduleId ?? "module"}-${lessonId ?? index}`,
        moduleId,
        moduleTitle,
        lessonId,
        lessonTitle: lessonTitle || moduleTitle || `Result ${index + 1}`,
        snippet,
        type,
        raw: item,
      };
    })
    .filter((entry, index, array) => {
      const firstIndex = array.findIndex((item) => item.key === entry.key);
      return firstIndex === index;
    });
};

// const contentTypeOptions = [
//   { value: "chapter_text", label: "Chapter Text" },
//   { value: "formative_assessment", label: "Formative Assessment" },
//   { value: "summative_assessment", label: "Summative Assessment" },
//   { value: "assignment", label: "Assignment" },
//   { value: "video", label: "Video" },
//   { value: "audio", label: "Audio" },
// ];

const normalizeTypeToken = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

const createSkillOptions = (value) => {
  const baseOptions = BLOOMS_TAXONOMY_SKILLS.map((option) => ({ ...option }));
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return baseOptions;
  }

  const exists = baseOptions.some(
    (option) =>
      option.value === normalizedValue || option.label === normalizedValue
  );

  if (exists) {
    return baseOptions;
  }

  return [...baseOptions, { value: normalizedValue, label: normalizedValue }];
};

const createCategoryOptions = (options, value) => {
  const baseOptions = Array.isArray(options)
    ? options.map((option) => ({ ...option }))
    : [];
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return baseOptions;
  }

  const exists = baseOptions.some(
    (option) =>
      option.value === normalizedValue || option.label === normalizedValue
  );

  if (exists) {
    return baseOptions;
  }

  return [{ value: normalizedValue, label: normalizedValue }, ...baseOptions];
};

const normalizeCategoryValue = (value) => String(value ?? "").trim();

const normalizeQuestionValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === "string") {
    return value.trim();
  }

  const numeric = Number(value);

  if (Number.isFinite(numeric)) {
    return String(numeric);
  }

  return "";
};

const normaliseCategoryEntries = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry) => ({
      category: normalizeCategoryValue(
        entry?.category ?? entry?.label ?? entry?.value ?? ""
      ),
      skilltype: normalizeCategoryValue(
        entry?.skilltype ?? entry?.skillType ?? ""
      ),
      questions: normalizeQuestionValue(
        entry?.questions ?? entry?.Questions ?? entry?.questionCount
      ),
    }))
    .filter((entry) => entry.category || entry.skilltype || entry.questions);
};

const ensureCategoryStateEntries = (
  entries,
  fallbackCategory = "",
  fallbackSkillType = DEFAULT_SKILL_TYPE,
  fallbackQuestions = ""
) => {
  const normalizedEntries = normaliseCategoryEntries(entries);

  if (normalizedEntries.length) {
    return normalizedEntries.map((entry) => ({
      category: entry.category,
      skilltype:
        entry.skilltype ||
        normalizeCategoryValue(fallbackSkillType) ||
        DEFAULT_SKILL_TYPE,
      questions: normalizeQuestionValue(entry.questions),
    }));
  }

  return [
    {
      category: normalizeCategoryValue(fallbackCategory),
      skilltype:
        normalizeCategoryValue(fallbackSkillType) || DEFAULT_SKILL_TYPE,
      questions: normalizeQuestionValue(fallbackQuestions),
    },
  ];
};

const formatCategoriesForPayload = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const seen = new Set();

  return entries
    .map((entry) => ({
      category: normalizeCategoryValue(entry?.category),
      skilltype: normalizeCategoryValue(entry?.skilltype ?? entry?.skillType),
      questions: normalizeQuestionValue(entry?.questions ?? entry?.Questions),
    }))
    .filter((entry) => {
      if (!entry.category || !entry.skilltype) {
        return false;
      }

      const key = `${entry.category.toLowerCase()}::${entry.skilltype.toLowerCase()}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

const getPrimaryCategoryFromEntries = (entries) => {
  const [first] = Array.isArray(entries) ? entries : [];

  if (!first) {
    return { category: "", skilltype: "" };
  }

  return {
    category: normalizeCategoryValue(first?.category),
    skilltype: normalizeCategoryValue(first?.skilltype ?? first?.skillType),
  };
};

const isCategoryCombinationDuplicate = (entries, category, skilltype) =>
  Array.isArray(entries)
    ? entries.some(
        (entry) =>
          normalizeCategoryValue(entry?.category) === category &&
          normalizeCategoryValue(entry?.skilltype ?? entry?.skillType) ===
            skilltype
      )
    : false;

const sanitizeCategoryEntries = (entries) =>
  Array.isArray(entries)
    ? entries
        .map((entry) => ({
          category: normalizeCategoryValue(
            entry?.category ?? entry?.label ?? entry?.value
          ),
          skilltype:
            normalizeCategoryValue(entry?.skilltype ?? entry?.skillType) ||
            DEFAULT_SKILL_TYPE,
          questions: normalizeQuestionValue(
            entry?.questions ?? entry?.Questions ?? entry?.questionCount
          ),
        }))
        .filter((entry) => entry.category && entry.skilltype)
    : [];

const extractAssessmentEntriesFromSettings = (settings) => {
  if (!settings || typeof settings !== "object") {
    return [];
  }

  const categoryEntries = settings.categories;

  if (!categoryEntries || typeof categoryEntries !== "object") {
    return [];
  }

  const entries = [];

  Object.entries(categoryEntries).forEach(([categoryKey, skillMap]) => {
    const normalizedCategory = normalizeCategoryValue(categoryKey);

    if (!normalizedCategory || !skillMap || typeof skillMap !== "object") {
      return;
    }

    Object.entries(skillMap).forEach(([skillKey, details]) => {
      const normalizedSkill = normalizeCategoryValue(skillKey);

      if (!normalizedSkill) {
        return;
      }

      const normalizedQuestions = normalizeQuestionValue(
        details?.questions ?? details?.Questions ?? details?.questionCount
      );

      entries.push({
        category: normalizedCategory,
        skilltype: normalizedSkill,
        questions: normalizedQuestions,
      });
    });
  });

  return entries;
};

const getLessonSequenceValue = (lesson) => {
  const candidates = [lesson?.seq, lesson?.sequence, lesson?.Sequence];

  for (const value of candidates) {
    const numeric = Number(value);

    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }

  return null;
};

const getLessonIdentifierForSort = (lesson) => {
  const identifier =
    lesson?.id ?? lesson?.lessonId ?? lesson?.LessonId ?? lesson?.name ?? "";

  return String(identifier);
};

const resolveLessonId = (lesson) =>
  lesson?.id ?? lesson?.lessonId ?? lesson?.LessonId ?? lesson?.name ?? "";

const normalizeNumericValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const numeric = Number(value);

  if (Number.isFinite(numeric)) {
    return numeric;
  }

  return undefined;
};

const sanitizePayload = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizePayload(entry))
      .filter((entry) => entry !== undefined);
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
      if (entryValue === undefined) {
        return accumulator;
      }

      const sanitizedValue = sanitizePayload(entryValue);

      if (sanitizedValue !== undefined) {
        accumulator[key] = sanitizedValue;
      }

      return accumulator;
    }, {});
  }

  return value;
};

const normalizeAssessmentEntries = (entries) => {
  const sanitizedEntries = formatCategoriesForPayload(entries);

  return sanitizedEntries.map((entry) => {
    const numericQuestions = normalizeNumericValue(entry.questions);

    return {
      category: entry.category,
      skilltype: entry.skilltype,
      questions: entry.questions,
      numericQuestions,
    };
  });
};

const formatAssessmentSettingsPayload = (entries) => {
  const normalizedEntries = normalizeAssessmentEntries(entries).filter(
    (entry) =>
      entry.category &&
      entry.skilltype &&
      entry.numericQuestions !== undefined &&
      Number.isFinite(entry.numericQuestions) &&
      entry.numericQuestions > 0
  );

  if (!normalizedEntries.length) {
    return null;
  }

  const categories = {};

  normalizedEntries.forEach((entry) => {
    if (!categories[entry.category]) {
      categories[entry.category] = {};
    }

    categories[entry.category][entry.skilltype] = {
      questions: Number(entry.numericQuestions),
    };
  });

  return { categories };
};

const hasInvalidAssessmentEntries = (entries) =>
  normalizeAssessmentEntries(entries).some(
    (entry) =>
      !entry.category ||
      !entry.skilltype ||
      entry.numericQuestions === undefined ||
      !Number.isFinite(entry.numericQuestions) ||
      entry.numericQuestions <= 0
  );

const buildLessonContentUpdatePayload = ({
  courseId,
  moduleId,
  chapterId,
  lesson,
  contentForm,
}) => {
  const resolvedLessonId = resolveLessonId(lesson);
  const hasLessonId =
    resolvedLessonId !== undefined &&
    resolvedLessonId !== null &&
    resolvedLessonId !== "";
  const resolvedChapterId =
    chapterId ?? lesson?.chapterId ?? DEFAULT_CHAPTER_ID;
  const contentCategoryEntries = Array.isArray(contentForm?.categories)
    ? contentForm.categories
    : undefined;
  const sanitizedLessonCategories = sanitizeCategoryEntries(lesson?.categories);
  const lessonSettingsEntries = extractAssessmentEntriesFromSettings(
    lesson?.settings
  );
  const fallbackCategorySource =
    (Array.isArray(contentCategoryEntries) && contentCategoryEntries[0]) ||
    sanitizedLessonCategories[0] ||
    lessonSettingsEntries[0] ||
    undefined;
  const fallbackCategory =
    normalizeCategoryValue(
      fallbackCategorySource?.category ??
        fallbackCategorySource?.label ??
        fallbackCategorySource?.value
    ) ||
    lesson?.category ||
    "";
  const fallbackSkillType =
    normalizeCategoryValue(
      fallbackCategorySource?.skilltype ?? fallbackCategorySource?.skillType
    ) ||
    lesson?.skillType ||
    lesson?.skilltype ||
    DEFAULT_SKILL_TYPE;
  const fallbackQuestions = normalizeQuestionValue(
    fallbackCategorySource?.questions
  );
  const categoryStateSource =
    Array.isArray(contentCategoryEntries) && contentCategoryEntries.length
      ? contentCategoryEntries
      : sanitizedLessonCategories.length
      ? sanitizedLessonCategories
      : lessonSettingsEntries;
  const normalizedCategoryState = ensureCategoryStateEntries(
    categoryStateSource,
    fallbackCategory,
    fallbackSkillType,
    fallbackQuestions
  );
  const normalizedCategories = formatCategoriesForPayload(
    normalizedCategoryState
  );
  const lessonTypeCandidate =
    contentForm?.lessonType ??
    contentForm?.contentType ??
    lesson?.lessonType ??
    lesson?.contentType ??
    lesson?.type ??
    "";
  const isAssessmentLesson = isAssessmentLessonTypeValue(lessonTypeCandidate);
  const assessmentEntriesSource =
    Array.isArray(contentCategoryEntries) && contentCategoryEntries.length
      ? contentCategoryEntries
      : lessonSettingsEntries.length
      ? lessonSettingsEntries
      : sanitizedLessonCategories;
  const assessmentSettings = isAssessmentLesson
    ? formatAssessmentSettingsPayload(assessmentEntriesSource)
    : null;
  const primaryCategory = getPrimaryCategoryFromEntries(normalizedCategories);
  const trimmedName =
    typeof contentForm?.Name === "string"
      ? contentForm.Name.trim()
      : contentForm?.Name;
  const trimmedTitle =
    typeof contentForm?.Title === "string"
      ? contentForm.Title.trim()
      : contentForm?.Title;
  const contentName =
    trimmedName || lesson?.name || lesson?.Name || lesson?.lessonName || "";
  const contentTitle =
    trimmedTitle || lesson?.title || lesson?.lessonTitle || lesson?.Title || "";
  const contentValue =
    typeof contentForm?.Value === "string"
      ? contentForm.Value
      : contentForm?.Value ?? "";
  const pageNumber =
    normalizeNumericValue(contentForm?.pageNumber) ??
    normalizeNumericValue(lesson?.pageNumber) ??
    1;

  const payload = {
    courseId: String(courseId ?? ""),
    moduleId: String(moduleId ?? ""),
    chapterId: String(resolvedChapterId ?? ""),
    lessonId: hasLessonId ? String(resolvedLessonId) : undefined,
    id: hasLessonId ? String(resolvedLessonId) : undefined,
    course_id: normalizeNumericValue(courseId) ?? String(courseId ?? ""),
    module_id: normalizeNumericValue(moduleId) ?? String(moduleId ?? ""),
    chapter_id:
      normalizeNumericValue(resolvedChapterId) ??
      String(resolvedChapterId ?? ""),
    lesson_id: hasLessonId
      ? normalizeNumericValue(resolvedLessonId) ?? String(resolvedLessonId)
      : undefined,
    name: contentName,
    title: contentTitle,
    description: contentForm?.description ?? lesson?.description ?? "",
    duration: normalizeNumericValue(
      contentForm?.duration ?? lesson?.duration ?? lesson?.Duration
    ),
    seq: normalizeNumericValue(
      contentForm?.seq ?? getLessonSequenceValue(lesson)
    ),
    isAssignment: Boolean(
      contentForm?.isAssignment ??
        lesson?.isAssignment ??
        lesson?.IsAssignment ??
        lesson?.assignment
    ),
    openForStudent: Boolean(
      contentForm?.openForStudent ?? lesson?.openForStudent
    ),
    contentType:
      contentForm?.contentType ??
      lesson?.contentType ??
      lesson?.lessonType ??
      lesson?.type ??
      "",
    lessonType:
      contentForm?.lessonType ??
      lesson?.lessonType ??
      lesson?.contentType ??
      lesson?.type ??
      "",
    categories: isAssessmentLesson ? undefined : normalizedCategories,
    category: primaryCategory.category || undefined,
    skillType: primaryCategory.skilltype || undefined,
    skilltype: primaryCategory.skilltype || undefined,
    grades: contentForm?.grades ?? lesson?.grades ?? lesson?.Grades,
    contentUri:
      contentForm?.contentUri ?? lesson?.contentUri ?? lesson?.ContentUri,
    Name: contentName,
    Title: contentTitle,
    Value: contentValue,
    value: contentValue,
    content: {
      Name: contentName,
      Title: contentTitle,
      Value: contentValue,
    },
    pageNumber,
    settings: assessmentSettings || undefined,
  };

  return sanitizePayload(payload);
};

const sortLessonsBySequence = (lessons) => {
  if (!Array.isArray(lessons)) {
    return [];
  }

  return [...lessons].sort((a, b) => {
    const seqA = getLessonSequenceValue(a);
    const seqB = getLessonSequenceValue(b);

    if (seqA !== null && seqB !== null && seqA !== seqB) {
      return seqA - seqB;
    }

    if (seqA !== null && seqB === null) {
      return -1;
    }

    if (seqA === null && seqB !== null) {
      return 1;
    }

    return getLessonIdentifierForSort(a).localeCompare(
      getLessonIdentifierForSort(b),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
};

const matchLessonTypeOption = (value) => {
  const normalized = normalizeTypeToken(value);

  if (!normalized) {
    return null;
  }

  return (
    LESSON_TYPE_OPTIONS.find(
      (option) =>
        normalizeTypeToken(option.value) === normalized ||
        normalizeTypeToken(option.label) === normalized
    ) ||
    LESSON_TYPE_OPTIONS.find((option) =>
      (option.aliases || []).some(
        (alias) => normalizeTypeToken(alias) === normalized
      )
    ) ||
    null
  );
};

const ASSESSMENT_TYPE_TOKENS = new Set(
  ["formative_assessment", "summative_assessment"].map((value) =>
    normalizeTypeToken(value)
  )
);

const ASSIGNMENT_TYPE_TOKENS = new Set(["assignment", "writing"]);

const isAssignmentLessonTypeValue = (value) => {
  if (!value) {
    return false;
  }

  const matchedOption = matchLessonTypeOption(value);
  const normalizedTokens = [];

  if (matchedOption) {
    normalizedTokens.push(
      normalizeTypeToken(matchedOption.value),
      normalizeTypeToken(matchedOption.label)
    );

    if (Array.isArray(matchedOption.aliases)) {
      matchedOption.aliases.forEach((alias) => {
        normalizedTokens.push(normalizeTypeToken(alias));
      });
    }
  } else {
    normalizedTokens.push(normalizeTypeToken(value));
  }

  return normalizedTokens.some(
    (token) => token && ASSIGNMENT_TYPE_TOKENS.has(token)
  );
};

const isAssignmentLessonTypeInfo = (typeInfo) => {
  if (!typeInfo) {
    return false;
  }

  if (isAssignmentLessonTypeValue(typeInfo.value)) {
    return true;
  }

  if (isAssignmentLessonTypeValue(typeInfo.label)) {
    return true;
  }

  if (Array.isArray(typeInfo.aliases)) {
    return typeInfo.aliases.some((alias) => isAssignmentLessonTypeValue(alias));
  }

  return false;
};

const isAssessmentLessonTypeValue = (value) => {
  const matchedOption = matchLessonTypeOption(value);
  const normalized = normalizeTypeToken(matchedOption?.value ?? value ?? "");

  if (!normalized) {
    return false;
  }

  return ASSESSMENT_TYPE_TOKENS.has(normalized);
};

const getLessonTypeForLesson = (lesson) => {
  if (!lesson) {
    return DEFAULT_LESSON_TYPE;
  }

  const candidateValues = [
    lesson.lessonType,
    lesson.type,
    lesson.category,
    lesson.skillType,
    lesson.contentType,
  ];

  if (Array.isArray(lesson.categories)) {
    lesson.categories.forEach((category) => {
      if (category?.category) {
        candidateValues.push(category.category);
      }
      if (category?.skilltype) {
        candidateValues.push(category.skilltype);
      }
    });
  }

  for (const value of candidateValues) {
    const match = matchLessonTypeOption(value);
    if (match) {
      return match;
    }
  }

  return DEFAULT_LESSON_TYPE;
};

const getLessonVisuals = (lesson) => {
  const typeInfo = getLessonTypeForLesson(lesson);
  const visuals =
    LESSON_TYPE_VISUALS[typeInfo.value] || LESSON_TYPE_VISUALS.default;

  return { ...visuals, label: typeInfo.label };
};

const LessonsManager = ({
  currentType,
  courses: controlledCourses,
  coursesLoading: controlledCoursesLoading,
  coursesError: controlledCoursesError,
  selectedCourseId: controlledSelectedCourseId,
  onSelectCourse: onControlledSelectCourse,
  onRefreshCourses: onControlledRefreshCourses,
  isNavigationCollapsed,
  onExpandNavigation,
  onCollapseNavigation,
  onToggleNavigationCollapse,
  setNavigationCollapsible,
}) => {
  const [internalCourses, setInternalCourses] = useState([]);
  const [internalCoursesLoading, setInternalCoursesLoading] = useState(true);
  const [internalCoursesError, setInternalCoursesError] = useState(null);
  const [internalSelectedCourseId, setInternalSelectedCourseId] =
    useState(null);

  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState(null);
  const [lessonsReordering, setLessonsReordering] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isEditingLessonContent, setIsEditingLessonContent] = useState(false);
  const shouldCollapseNavigationRef = useRef(false);

  const [courseSearchQuery, setCourseSearchQuery] = useState("");
  const [courseSearchModuleId, setCourseSearchModuleId] = useState("");
  const [courseSearchResults, setCourseSearchResults] = useState([]);
  const [courseSearchLoading, setCourseSearchLoading] = useState(false);
  const [courseSearchError, setCourseSearchError] = useState(null);
  const [courseSearchPerformed, setCourseSearchPerformed] = useState(false);
  const [courseSearchActiveKey, setCourseSearchActiveKey] = useState(null);
  const courseSearchRequestRef = useRef(0);
  const pendingSearchSelectionRef = useRef(null);

  const [lessonContent, setLessonContent] = useState(null);
  const [lessonContentLoading, setLessonContentLoading] = useState(false);
  const [lessonContentError, setLessonContentError] = useState(null);

  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleDialogError, setModuleDialogError] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    id: "",
    moduleId: "",
    name: "",
    title: "",
    description: "",
    objective: "",
    mediaUrl: "",
    scheduledDate: "",
    duration: "",
  });
  const [savingModule, setSavingModule] = useState(false);

  const [lessonFlowOpen, setLessonFlowOpen] = useState(false);
  const [lessonFlowStep, setLessonFlowStep] = useState(0);
  const [lessonFlowError, setLessonFlowError] = useState(null);
  const lessonDescriptionContainerRef = useRef(null);
  const lessonMetadataDescriptionContainerRef = useRef(null);
  const [isLessonDescriptionEditing, setIsLessonDescriptionEditing] =
    useState(false);
  const [
    isLessonMetadataDescriptionEditing,
    setIsLessonMetadataDescriptionEditing,
  ] = useState(false);
  const [lessonForm, setLessonForm] = useState({
    name: "",
    duration: "",
    grades: "",
    seq: "",
    title: "",
    description: "",
    contentType: "",
    isAssignment: false,
    openForStudent: false,
    contentUri: "",
    categories: [],
    lessonType: LESSON_TYPE_PLACEHOLDER.value,
  });
  const [lessonFlowSaving, setLessonFlowSaving] = useState(false);
  const [newLessonContent, setNewLessonContent] = useState({
    Name: "",
    Title: "",
    Value: "",
  });

  const [contentForm, setContentForm] = useState({
    Name: "",
    Title: "",
    Value: "",
    pageNumber: 1, // ✅ ADD THIS
  });

  const [lessonMetadataDialogOpen, setLessonMetadataDialogOpen] =
    useState(false);
  const [lessonMetadataForm, setLessonMetadataForm] = useState({
    name: "",
    title: "",
    duration: "",
    grades: "",
    seq: "",
    description: "",
    lessonType: LESSON_TYPE_PLACEHOLDER.value,
    categories: [],
    isAssignment: false,
    openForStudent: false,
  });
  const sanitizedLessonMetadataDescription = useMemo(
    () => sanitizeLessonDescriptionHtml(lessonMetadataForm.description),
    [lessonMetadataForm.description]
  );
  const hasLessonMetadataDescription = useMemo(
    () => Boolean(stripHtmlTags(sanitizedLessonMetadataDescription)),
    [sanitizedLessonMetadataDescription]
  );
  const hasLessonDescription = useMemo(
    () => Boolean(stripHtmlTags(lessonForm.description)),
    [lessonForm.description]
  );
  const isLessonFormAssessment = useMemo(
    () => isAssessmentLessonTypeValue(lessonForm.lessonType),
    [lessonForm.lessonType]
  );
  const [lessonFormCategoryDraft, setLessonFormCategoryDraft] = useState({
    category: "",
    skilltype: DEFAULT_SKILL_TYPE,
    questions: "",
  });
  const [lessonMetadataCategoryDraft, setLessonMetadataCategoryDraft] =
    useState({
      category: "",
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    });
  const [lessonMetadataTarget, setLessonMetadataTarget] = useState(null);
  const [savingLessonMetadata, setSavingLessonMetadata] = useState(false);
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [taxonomyError, setTaxonomyError] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addingPage, setAddingPage] = useState(false);
  const [isNewPage, setIsNewPage] = useState(false);

  const [courseDetailTab, setCourseDetailTab] = useState("overview");
  const shouldSkipLessonContentStep =
    isLessonFormAssessment || Boolean(lessonForm.isAssignment);
  const lessonFlowSteps = useMemo(() => {
    const steps = [
      { key: "type", label: "Choose type" },
      { key: "details", label: "Basic info" },
    ];

    if (!shouldSkipLessonContentStep) {
      steps.push({ key: "content", label: "Content" });
    }

    steps.push({ key: "review", label: "Review & save" });

    return steps;
  }, [shouldSkipLessonContentStep]);
  useEffect(() => {
    setLessonFlowStep((prev) =>
      Math.min(prev, Math.max(lessonFlowSteps.length - 1, 0))
    );
  }, [lessonFlowSteps.length]);
  const currentLessonFlowStepKey = lessonFlowSteps[lessonFlowStep]?.key;

  const defaultCourseForm = useMemo(
    () => ({
      id: "",
      name: "",
      description: "",
      mediaUrl: "",
      startDate: "",
      active: true,
      locked: "no",
      continueOnReconnect: true,
      maxTimeBeforeReset: 12000,
    }),
    []
  );
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [courseDialogError, setCourseDialogError] = useState(null);
  const [savingCourse, setSavingCourse] = useState(false);
  const [courseForm, setCourseForm] = useState(defaultCourseForm);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const lessonMetadataCategoryChips = useMemo(() => {
    if (!Array.isArray(lessonMetadataForm.categories)) {
      return [];
    }

    return lessonMetadataForm.categories
      .map((entry, index) => {
        const category = normalizeCategoryValue(
          entry?.category ?? entry?.label ?? entry?.value
        );
        const skilltype = normalizeCategoryValue(
          entry?.skilltype ?? entry?.skillType
        );
        const questions = normalizeQuestionValue(entry?.questions);

        if (!category || !skilltype) {
          return null;
        }

        return { category, skilltype, questions, index };
      })
      .filter(Boolean);
  }, [lessonMetadataForm.categories]);

  const lessonFormCategoryChips = useMemo(() => {
    if (!Array.isArray(lessonForm.categories)) {
      return [];
    }

    return lessonForm.categories
      .map((entry, index) => {
        const category = normalizeCategoryValue(
          entry?.category ?? entry?.label ?? entry?.value
        );
        const skilltype = normalizeCategoryValue(
          entry?.skilltype ?? entry?.skillType
        );
        const questions = normalizeQuestionValue(entry?.questions);

        if (!category || !skilltype) {
          return null;
        }

        return { category, skilltype, questions, index };
      })
      .filter(Boolean);
  }, [lessonForm.categories]);

  const getCategoryLabel = useCallback(
    (value) => {
      const normalizedValue = normalizeCategoryValue(value);

      if (!normalizedValue) {
        return "";
      }

      const matched = taxonomyOptions.find(
        (option) =>
          normalizeCategoryValue(option?.value) === normalizedValue ||
          normalizeCategoryValue(option?.label) === normalizedValue
      );

      return matched?.label || normalizedValue;
    },
    [taxonomyOptions]
  );

  const normalizedLessonMetadataDraft = useMemo(
    () => ({
      category: normalizeCategoryValue(lessonMetadataCategoryDraft.category),
      skilltype:
        normalizeCategoryValue(lessonMetadataCategoryDraft.skilltype) ||
        DEFAULT_SKILL_TYPE,
      questions: normalizeQuestionValue(lessonMetadataCategoryDraft.questions),
      numericQuestions: normalizeNumericValue(
        normalizeQuestionValue(lessonMetadataCategoryDraft.questions)
      ),
    }),
    [lessonMetadataCategoryDraft]
  );

  const normalizedLessonFormDraft = useMemo(
    () => ({
      category: normalizeCategoryValue(lessonFormCategoryDraft.category),
      skilltype:
        normalizeCategoryValue(lessonFormCategoryDraft.skilltype) ||
        DEFAULT_SKILL_TYPE,
      questions: normalizeQuestionValue(lessonFormCategoryDraft.questions),
      numericQuestions: normalizeNumericValue(
        normalizeQuestionValue(lessonFormCategoryDraft.questions)
      ),
    }),
    [lessonFormCategoryDraft]
  );

  const isLessonMetadataAssessment = useMemo(
    () => isAssessmentLessonTypeValue(lessonMetadataForm.lessonType),
    [lessonMetadataForm.lessonType]
  );

  const canAddLessonMetadataCategory =
    Boolean(normalizedLessonMetadataDraft.category) &&
    Boolean(normalizedLessonMetadataDraft.skilltype) &&
    (!isLessonMetadataAssessment ||
      (normalizedLessonMetadataDraft.questions &&
        normalizedLessonMetadataDraft.numericQuestions !== undefined &&
        Number.isFinite(normalizedLessonMetadataDraft.numericQuestions) &&
        normalizedLessonMetadataDraft.numericQuestions > 0)) &&
    !isCategoryCombinationDuplicate(
      lessonMetadataForm.categories,
      normalizedLessonMetadataDraft.category,
      normalizedLessonMetadataDraft.skilltype
    );

  const canAddLessonFormCategory =
    Boolean(normalizedLessonFormDraft.category) &&
    Boolean(normalizedLessonFormDraft.skilltype) &&
    (!isLessonFormAssessment ||
      (normalizedLessonFormDraft.questions &&
        normalizedLessonFormDraft.numericQuestions !== undefined &&
        Number.isFinite(normalizedLessonFormDraft.numericQuestions) &&
        normalizedLessonFormDraft.numericQuestions > 0)) &&
    !isCategoryCombinationDuplicate(
      lessonForm.categories,
      normalizedLessonFormDraft.category,
      normalizedLessonFormDraft.skilltype
    );

  useEffect(() => {
    if (!lessonMetadataDialogOpen && isLessonMetadataDescriptionEditing) {
      setIsLessonMetadataDescriptionEditing(false);
    }
  }, [isLessonMetadataDescriptionEditing, lessonMetadataDialogOpen]);

  const isCoursesControlled =
    controlledCourses !== undefined ||
    controlledCoursesLoading !== undefined ||
    controlledCoursesError !== undefined ||
    controlledSelectedCourseId !== undefined ||
    typeof onControlledSelectCourse === "function" ||
    typeof onControlledRefreshCourses === "function";

  const courses = isCoursesControlled
    ? controlledCourses || []
    : internalCourses;
  const coursesLoading = isCoursesControlled
    ? Boolean(controlledCoursesLoading)
    : internalCoursesLoading;
  const coursesError = isCoursesControlled
    ? controlledCoursesError || null
    : internalCoursesError;
  const selectedCourseId = isCoursesControlled
    ? controlledSelectedCourseId ?? null
    : internalSelectedCourseId;

  const loadCourses = useCallback(async () => {
    if (isCoursesControlled) {
      return;
    }

    setInternalCoursesLoading(true);
    setInternalCoursesError(null);
    try {
      const response = await LessonAuthoringService.fetchCourses();
      const normalized = Array.isArray(response) ? response : [];
      setInternalCourses(normalized);
      setInternalSelectedCourseId((previous) => {
        if (
          previous !== null &&
          normalized.some(
            (course) =>
              String(course?.id) === String(previous) &&
              Number(course?.roleId) === 2 &&
              course?.enrollmentActiive === true
          )
        ) {
          return previous;
        }

        const defaultCourse = normalized.find(
          (course) =>
            Number(course?.roleId) === 2 && course?.enrollmentActiive === true
        );

        return defaultCourse ? defaultCourse.id : null;
      });
    } catch (error) {
      console.error("Failed to fetch courses", error);
      setInternalCoursesError(
        error?.message ||
          "Unable to load courses right now. Please try again later."
      );
      setInternalCourses([]);
      setInternalSelectedCourseId(null);
    } finally {
      setInternalCoursesLoading(false);
    }
  }, [isCoursesControlled]);

  useEffect(() => {
    if (isCoursesControlled) {
      return;
    }

    loadCourses();
  }, [isCoursesControlled, loadCourses]);

  useEffect(() => {
    let isMounted = true;

    const fetchTaxonomy = async () => {
      if (!ItemService.isConfigured()) {
        if (isMounted) {
          setTaxonomyOptions([]);
          setTaxonomyError("Items API URL is not configured.");
          setTaxonomyLoading(false);
        }
        return;
      }

      setTaxonomyLoading(true);
      setTaxonomyError(null);

      try {
        const taxonomyResponse = await ItemService.fetchTaxonomy("arabic");

        if (!isMounted) {
          return;
        }

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setTaxonomyOptions([]);
        setTaxonomyError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load taxonomy."
        );
      } finally {
        if (isMounted) {
          setTaxonomyLoading(false);
        }
      }
    };

    fetchTaxonomy();

    return () => {
      isMounted = false;
    };
  }, []);

  const instructorCourses = useMemo(
    () =>
      courses.filter((course) => {
        const roleId = Number(course?.roleId);
        const isActive = course?.enrollmentActiive === true;
        return roleId === 2 && isActive;
      }),
    [courses]
  );

  const taxonomyHelperText = useMemo(() => {
    if (taxonomyLoading) {
      return "Loading categories…";
    }

    if (taxonomyError) {
      return taxonomyError;
    }

    return undefined;
  }, [taxonomyError, taxonomyLoading]);

  useEffect(() => {
    const hasLessonSelection = Boolean(selectedLesson);
    const shouldCollapseNavigation = hasLessonSelection;
    const hadSelection = shouldCollapseNavigationRef.current;

    if (typeof setNavigationCollapsible === "function") {
      setNavigationCollapsible(shouldCollapseNavigation);
    }

    if (shouldCollapseNavigation && !hadSelection) {
      if (typeof onCollapseNavigation === "function") {
        onCollapseNavigation();
      } else if (
        typeof onToggleNavigationCollapse === "function" &&
        !isNavigationCollapsed
      ) {
        onToggleNavigationCollapse();
      }
    }

    if (!shouldCollapseNavigation && hadSelection) {
      if (typeof onExpandNavigation === "function") {
        onExpandNavigation();
      } else if (
        typeof onToggleNavigationCollapse === "function" &&
        isNavigationCollapsed
      ) {
        onToggleNavigationCollapse();
      }
    }

    shouldCollapseNavigationRef.current = shouldCollapseNavigation;
  }, [
    isNavigationCollapsed,
    onCollapseNavigation,
    onExpandNavigation,
    onToggleNavigationCollapse,
    selectedModuleId,
    selectedLesson,
    setNavigationCollapsible,
  ]);

  useEffect(() => {
    if (!selectedCourseId) {
      setModules([]);
      setSelectedModuleId(null);
      return;
    }

    const loadModules = async () => {
      setModulesLoading(true);
      setModulesError(null);
      try {
        const data = await LessonAuthoringService.fetchModules(
          selectedCourseId
        );
        setModules(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load modules", error);
        setModulesError(
          error?.message || "Unable to load modules for this course."
        );
      } finally {
        setModulesLoading(false);
      }
    };

    loadModules();
  }, [selectedCourseId]);

  useEffect(() => {
    courseSearchRequestRef.current += 1;
    setCourseSearchQuery("");
    setCourseSearchModuleId("");
    setCourseSearchResults([]);
    setCourseSearchError(null);
    setCourseSearchLoading(false);
    setCourseSearchPerformed(false);
    setCourseSearchActiveKey(null);
    pendingSearchSelectionRef.current = null;
  }, [selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId || !selectedModuleId) {
      setLessons([]);
      setSelectedLesson(null);
      return;
    }

    const loadLessons = async () => {
      setLessonsLoading(true);
      setLessonsError(null);
      try {
        const data = await LessonAuthoringService.fetchLessons(
          selectedCourseId,
          selectedModuleId
        );
        setLessons(sortLessonsBySequence(data));
      } catch (error) {
        console.error("Failed to load lessons", error);
        setLessonsError(
          error?.message || "Unable to load lessons for this module."
        );
      } finally {
        setLessonsLoading(false);
      }
    };

    loadLessons();
  }, [selectedCourseId, selectedModuleId]);

  useEffect(() => {
    if (!selectedModuleId) {
      return;
    }

    const pendingSelection = pendingSearchSelectionRef.current;

    if (!pendingSelection) {
      return;
    }

    const pendingModuleId = pendingSelection.moduleId;

    if (
      pendingModuleId !== null &&
      pendingModuleId !== undefined &&
      String(pendingModuleId) !== String(selectedModuleId)
    ) {
      return;
    }

    if (!pendingSelection.lessonId) {
      pendingSearchSelectionRef.current = null;
      return;
    }

    if (lessonsLoading) {
      return;
    }

    const matchedLesson = lessons.find((lesson) => {
      const lessonIdentifier = resolveLessonId(lesson);

      if (lessonIdentifier === undefined || lessonIdentifier === null) {
        return false;
      }

      return String(lessonIdentifier) === String(pendingSelection.lessonId);
    });

    if (matchedLesson) {
      setSelectedLesson(matchedLesson);
      setIsEditingLessonContent(false);
      pendingSearchSelectionRef.current = null;
    } else {
      pendingSearchSelectionRef.current = null;
    }
  }, [
    lessons,
    lessonsLoading,
    selectedModuleId,
    setIsEditingLessonContent,
    setSelectedLesson,
  ]);

  useEffect(() => {
    if (!selectedCourseId || !selectedModuleId || !selectedLesson) {
      setLessonContent(null);
      return;
    }

    const loadLessonContent = async () => {
      setLessonContentLoading(true);
      setLessonContentError(null);

      try {
        const data = await LessonAuthoringService.fetchLessonContent(
          selectedCourseId,
          selectedModuleId,
          selectedLesson?.chapterId || DEFAULT_CHAPTER_ID,
          resolveLessonId(selectedLesson),
          currentPage
        );

        setLessonContent(data || null);

        // ✅ SYNC page info FROM backend (same as old app)
        setTotalPages(data?.TotalPages ?? 1);
        setCurrentPage(data?.CurPgNum ?? currentPage);

        // ✅ Populate editor ONLY from backend
        setContentForm({
          Name: data?.Name || "",
          Title: data?.Title || "",
          Value: data?.Value || "",
        });
      } catch (error) {
        console.error("Failed to load lesson content", error);
        setLessonContentError(
          error?.message ||
            "Unable to load the selected lesson content right now."
        );
      } finally {
        setLessonContentLoading(false);
      }
    };

    loadLessonContent();
  }, [selectedCourseId, selectedModuleId, selectedLesson, currentPage]);

  useEffect(() => {
    if (!lessonContent) {
      setContentForm({
        Name: "",
        Title: "",
        Value: "",
        pageNumber: 1,
      });
      return;
    }

    setContentForm({
      Name: lessonContent?.Name || "",
      Title: lessonContent?.Title || "",
      Value: lessonContent?.Value || "",
      pageNumber: lessonContent?.pageNumber ?? 1, // ✅ IMPORTANT
    });
  }, [lessonContent]);

  const PageSelector = ({ currentPage, totalPages, onChange }) => (
    <FormControl size="small">
      <Select
        value={currentPage}
        onChange={(e) => onChange(Number(e.target.value))}
        renderValue={(value) => `Page ${value}`}
      >
        {Array.from({ length: totalPages }).map((_, i) => (
          <MenuItem key={i + 1} value={i + 1}>
            Page {i + 1}
          </MenuItem>
        ))}
        <MenuItem value={-1}>➕ Add Page</MenuItem>
      </Select>
    </FormControl>
  );

  const handlePageChange = (value) => {
    if (value === -1) {
      const nextPage = totalPages + 1;

      // 🔥 frontend-only page switch
      setCurrentPage(nextPage);
      setTotalPages((p) => p + 1);

      // empty editor for new page
      setContentForm((prev) => ({
        Name: prev.Name,
        Title: prev.Title,
        Value: "",
      }));

      return;
    }

    setCurrentPage(value);
  };

  const handleSelectCourse = (courseId) => {
    setSelectedModuleId(null);
    setSelectedLesson(null);
    setIsEditingLessonContent(false);
    setCourseDetailTab("overview");
    handleCloseLessonMetadataDialog();

    if (isCoursesControlled) {
      if (typeof onControlledSelectCourse === "function") {
        onControlledSelectCourse(courseId);
      }
    } else {
      setInternalSelectedCourseId(courseId);
    }
  };

  const handleRefreshCourses = () => {
    if (isCoursesControlled) {
      if (typeof onControlledRefreshCourses === "function") {
        return onControlledRefreshCourses();
      }

      return undefined;
    }

    return loadCourses();
  };

  const handleCourseSearchModuleChange = useCallback((event) => {
    const value = event?.target?.value ?? "";
    setCourseSearchModuleId(value);
  }, []);

  const handleClearCourseSearch = useCallback(() => {
    courseSearchRequestRef.current += 1;
    setCourseSearchQuery("");
    setCourseSearchResults([]);
    setCourseSearchError(null);
    setCourseSearchLoading(false);
    setCourseSearchPerformed(false);
    setCourseSearchActiveKey(null);
    pendingSearchSelectionRef.current = null;
  }, []);

  const handleCourseSearchSubmit = useCallback(
    async (event) => {
      event?.preventDefault();

      if (!selectedCourseId) {
        setCourseSearchError("Select a course before searching its content.");
        setCourseSearchPerformed(false);
        return;
      }

      const trimmedQuery = courseSearchQuery.trim();

      if (!trimmedQuery) {
        setCourseSearchError(
          "Enter a search term to search the course content."
        );
        setCourseSearchResults([]);
        setCourseSearchPerformed(false);
        setCourseSearchActiveKey(null);
        return;
      }

      const requestId = (courseSearchRequestRef.current += 1);

      setCourseSearchLoading(true);
      setCourseSearchError(null);
      setCourseSearchPerformed(true);

      try {
        const response = await LessonAuthoringService.searchCourseContent(
          selectedCourseId,
          trimmedQuery,
          courseSearchModuleId ? courseSearchModuleId : undefined
        );

        if (courseSearchRequestRef.current !== requestId) {
          return;
        }

        const normalized = normaliseCourseSearchResults(response);
        setCourseSearchResults(normalized);
        setCourseSearchActiveKey(null);
      } catch (error) {
        if (courseSearchRequestRef.current !== requestId) {
          return;
        }

        console.error("Failed to search course content", error);
        setCourseSearchError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to search the course content right now."
        );
        setCourseSearchResults([]);
        setCourseSearchActiveKey(null);
      } finally {
        if (courseSearchRequestRef.current === requestId) {
          setCourseSearchLoading(false);
        }
      }
    },
    [courseSearchModuleId, courseSearchQuery, selectedCourseId]
  );

  const handleSelectModule = (module) => {
    const moduleIdentifier = module?.id ?? module?.moduleId;

    if (moduleIdentifier === undefined || moduleIdentifier === null) {
      setSelectedModuleId(null);
      setSelectedLesson(null);
      setIsEditingLessonContent(false);
      handleCloseLessonMetadataDialog();
      return;
    }

    setSelectedModuleId(moduleIdentifier);
    setSelectedLesson(null);
    setIsEditingLessonContent(false);
    setCourseDetailTab("modules");
    handleCloseLessonMetadataDialog();
  };

  const handleCourseSearchResultClick = useCallback(
    (result) => {
      if (!result) {
        return;
      }

      const moduleId =
        result.moduleId ??
        extractIdentifierValue(result.raw?.moduleId) ??
        extractIdentifierValue(result.raw?.module_id) ??
        extractIdentifierValue(result.raw?.moduleid) ??
        extractIdentifierValue(result.raw?.module) ??
        null;

      const lessonId =
        result.lessonId ??
        extractIdentifierValue(result.raw?.lessonId) ??
        extractIdentifierValue(result.raw?.lesson_id) ??
        extractIdentifierValue(result.raw?.lessonid) ??
        extractIdentifierValue(result.raw?.lesson) ??
        extractIdentifierValue(result.raw?.contentId) ??
        extractIdentifierValue(result.raw?.content_id) ??
        extractIdentifierValue(result.raw?.id) ??
        null;

      if (!moduleId && !lessonId) {
        return;
      }

      pendingSearchSelectionRef.current = {
        moduleId: moduleId ?? null,
        lessonId: lessonId ?? null,
      };

      if (moduleId) {
        const matchedModule = modules.find((module) => {
          const moduleIdentifier = module?.id ?? module?.moduleId;

          if (moduleIdentifier === undefined || moduleIdentifier === null) {
            return false;
          }

          return String(moduleIdentifier) === String(moduleId);
        });

        if (matchedModule) {
          handleSelectModule(matchedModule);
        } else {
          setSelectedModuleId(moduleId);
          setSelectedLesson(null);
          setIsEditingLessonContent(false);
          setCourseDetailTab("modules");
        }
      } else {
        setCourseDetailTab("modules");
      }

      if (lessonId && !moduleId) {
        pendingSearchSelectionRef.current = {
          moduleId: null,
          lessonId,
        };
      }

      setCourseSearchActiveKey(result.key);
    },
    [
      handleSelectModule,
      modules,
      setCourseDetailTab,
      setIsEditingLessonContent,
      setSelectedLesson,
      setSelectedModuleId,
    ]
  );

  const handleReturnToCourse = () => {
    setSelectedModuleId(null);
    setSelectedLesson(null);
    setIsEditingLessonContent(false);
    setCourseDetailTab("modules");
    handleCloseLessonMetadataDialog();
  };

  const handleSelectLesson = (lesson, options = {}) => {
    const { enterEditMode = false } = options;
    handleCloseLessonMetadataDialog();
    setSelectedLesson(lesson);
    setIsEditingLessonContent(Boolean(enterEditMode));
  };

  const handleExitLessonEditor = useCallback(() => {
    setIsEditingLessonContent(false);
  }, []);

  const handleOpenModuleDialog = (module = null) => {
    if (module) {
      const moduleIdentifier = module?.id ?? module?.moduleId;
      const normalizedIdentifier =
        moduleIdentifier !== undefined && moduleIdentifier !== null
          ? String(moduleIdentifier)
          : null;
      setModuleForm({
        id: module?.id ?? "",
        moduleId: module?.moduleId ?? normalizedIdentifier ?? module?.id ?? "",
        name: module?.name || "",
        title: module?.title || "",
        description: module?.description || "",
        objective: module?.objective || "",
        mediaUrl: module?.mediaUrl || "",
        scheduledDate: module?.scheduledDate || "",
        duration: module?.duration || "",
      });
      setEditingModuleId(normalizedIdentifier);
    } else {
      setModuleForm({
        id: "",
        moduleId: "",
        name: "",
        title: "",
        description: "",
        objective: "",
        mediaUrl: "",
        scheduledDate: "",
        duration: "",
      });
      setEditingModuleId(null);
    }
    setModuleDialogError(null);
    setModuleDialogOpen(true);
  };

  const handleCloseModuleDialog = () => {
    setModuleDialogOpen(false);
    setEditingModuleId(null);
  };

  const handleModuleFormChange = (field) => (event) => {
    setModuleForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmitModule = async () => {
    if (!selectedCourseId) {
      setModuleDialogError("Please select a course before creating a module.");
      return;
    }

    const fallbackModuleId = moduleForm.moduleId || moduleForm.id;
    const normalizedModuleId =
      typeof fallbackModuleId === "string"
        ? fallbackModuleId.trim()
        : fallbackModuleId;

    if (
      normalizedModuleId === undefined ||
      normalizedModuleId === null ||
      normalizedModuleId === ""
    ) {
      setModuleDialogError("Module ID is required.");
      return;
    }

    const trimmedName = moduleForm.name.trim();

    if (!trimmedName) {
      setModuleDialogError("Module name is required.");
      return;
    }

    const trimmedDisplayId =
      typeof moduleForm.id === "string" ? moduleForm.id.trim() : moduleForm.id;

    const payload = {
      courseId: String(selectedCourseId),
      moduleId: String(normalizedModuleId),
      description: moduleForm.description,
      id:
        trimmedDisplayId === undefined ||
        trimmedDisplayId === null ||
        trimmedDisplayId === ""
          ? String(normalizedModuleId)
          : String(trimmedDisplayId),
      mediaUrl: moduleForm.mediaUrl,
      name: trimmedName,
      objective: moduleForm.objective,
      scheduledDate: moduleForm.scheduledDate,
      title: moduleForm.title,
    };

    if (moduleForm.duration) {
      payload.duration = Number(moduleForm.duration);
    }

    const isEditingModule = editingModuleId !== null;

    setSavingModule(true);
    setModuleDialogError(null);
    try {
      if (isEditingModule) {
        await LessonAuthoringService.updateModule(
          selectedCourseId,
          normalizedModuleId,
          payload
        );
      } else {
        await LessonAuthoringService.createModule(payload);
      }
      setModuleDialogOpen(false);
      setEditingModuleId(null);
      setSnackbar({
        open: true,
        message: isEditingModule
          ? "Module updated successfully."
          : "Module created successfully.",
        severity: "success",
      });
      const refreshedModules = await LessonAuthoringService.fetchModules(
        selectedCourseId
      );
      setModules(Array.isArray(refreshedModules) ? refreshedModules : []);
    } catch (error) {
      console.error("Failed to save module", error);
      setModuleDialogError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save the module."
      );
    } finally {
      setSavingModule(false);
    }
  };

  const handleOpenLessonFlow = () => {
    setLessonForm({
      name: "",
      duration: "",
      grades: "",
      seq: "",
      title: "",
      description: "",
      contentType: "",
      isAssignment: false,
      openForStudent: false,
      contentUri: "",
      categories: [],
      lessonType: LESSON_TYPE_PLACEHOLDER.value,
    });
    setNewLessonContent({ Name: "", Title: "", Value: "" });
    setLessonFormCategoryDraft({
      category: "",
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    });
    setLessonFlowError(null);
    setLessonFlowStep(0);
    setLessonFlowOpen(true);
  };

  const handleLessonFormChange = (field) => (eventOrValue) => {
    let value;

    if (
      eventOrValue &&
      typeof eventOrValue === "object" &&
      "target" in eventOrValue &&
      eventOrValue.target
    ) {
      const { target } = eventOrValue;
      if (target.type === "checkbox") {
        value = Boolean(target.checked);
      } else {
        value = target.value;
      }
    } else {
      value = eventOrValue;
    }

    if (field === "lessonType") {
      const matchedType = matchLessonTypeOption(value) || DEFAULT_LESSON_TYPE;
      setLessonForm((prev) => {
        const previousType =
          matchLessonTypeOption(prev.lessonType) || DEFAULT_LESSON_TYPE;
        const existingCategories = Array.isArray(prev.categories)
          ? prev.categories.map((entry) => ({ ...entry }))
          : [];

        if (!existingCategories.length) {
          return {
            ...prev,
            lessonType: matchedType.value,
            contentType: matchedType.value,
            categories: [],
          };
        }

        const updatedCategories = existingCategories.map((entry, index) => {
          if (index !== 0) {
            return entry;
          }

          const updatedEntry = { ...entry };

          if (!normalizeCategoryValue(updatedEntry.skilltype)) {
            updatedEntry.skilltype = DEFAULT_SKILL_TYPE;
          }

          if (
            normalizeCategoryValue(updatedEntry.category) === previousType.label
          ) {
            updatedEntry.category = "";
          }

          return updatedEntry;
        });

        return {
          ...prev,
          lessonType: matchedType.value,
          contentType: matchedType.value,
          categories: updatedCategories,
        };
      });
      return;
    }

    setLessonForm((prev) => ({ ...prev, [field]: value ?? "" }));
  };

  const handleLessonFormCategoryDraftChange = (field) => (eventOrValue) => {
    const value =
      typeof eventOrValue === "object" &&
      eventOrValue !== null &&
      "target" in eventOrValue
        ? eventOrValue.target?.value ?? ""
        : eventOrValue ?? "";

    setLessonFormCategoryDraft((prev) => ({
      ...prev,
      [field]: typeof value === "string" ? value : String(value ?? ""),
    }));
  };

  const handleAddLessonFormCategory = () => {
    const normalizedCategory = normalizeCategoryValue(
      lessonFormCategoryDraft.category
    );
    const normalizedSkilltype =
      normalizeCategoryValue(lessonFormCategoryDraft.skilltype) ||
      DEFAULT_SKILL_TYPE;
    const normalizedQuestions = normalizeQuestionValue(
      lessonFormCategoryDraft.questions
    );
    const numericQuestions = normalizeNumericValue(normalizedQuestions);
    const isAssessmentLesson = isAssessmentLessonTypeValue(
      lessonForm.lessonType
    );

    if (!normalizedCategory || !normalizedSkilltype) {
      return;
    }

    if (
      isAssessmentLesson &&
      (normalizedQuestions === "" ||
        numericQuestions === undefined ||
        !Number.isFinite(numericQuestions) ||
        numericQuestions <= 0)
    ) {
      return;
    }

    setLessonForm((prev) => {
      const categories = Array.isArray(prev.categories)
        ? prev.categories.map((entry) => ({ ...entry }))
        : [];

      if (
        isCategoryCombinationDuplicate(
          categories,
          normalizedCategory,
          normalizedSkilltype
        )
      ) {
        return prev;
      }

      const newEntry = {
        category: normalizedCategory,
        skilltype: normalizedSkilltype,
      };

      if (isAssessmentLesson) {
        newEntry.questions = String(Number(numericQuestions));
      }

      return {
        ...prev,
        categories: [...categories, newEntry],
      };
    });

    setLessonFormCategoryDraft((prev) => ({
      category: normalizedCategory,
      skilltype: normalizeCategoryValue(prev?.skilltype) || DEFAULT_SKILL_TYPE,
      questions: "",
    }));
  };

  const handleRemoveLessonFormCategory = (index) => () => {
    setLessonForm((prev) => {
      const categories = Array.isArray(prev.categories)
        ? prev.categories.filter((_, itemIndex) => itemIndex !== index)
        : [];

      return { ...prev, categories };
    });
  };

  const handleLessonDragEnd = useCallback(
    async (result) => {
      const sourceIndex = result?.source?.index;
      const destinationIndex = result?.destination?.index;

      if (
        destinationIndex === undefined ||
        sourceIndex === undefined ||
        destinationIndex === sourceIndex
      ) {
        return;
      }

      if (!selectedCourseId || !selectedModuleId) {
        return;
      }

      const previousLessons = lessons.slice();
      const sourceLesson = lessons[sourceIndex];

      if (!sourceLesson) {
        return;
      }

      const destinationLessonBeforeMove = lessons[destinationIndex] || null;
      const reorderedLessons = previousLessons.slice();
      const [movedLesson] = reorderedLessons.splice(sourceIndex, 1);

      if (!movedLesson) {
        return;
      }

      reorderedLessons.splice(destinationIndex, 0, movedLesson);
      setLessons(reorderedLessons);

      const toPayloadValue = (value) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) {
          return numeric;
        }

        return String(value ?? "");
      };

      const resolveChapterId = (lesson) => {
        const candidates = [lesson?.chapterId, lesson?.ChapterId];

        for (const candidate of candidates) {
          if (
            candidate !== undefined &&
            candidate !== null &&
            candidate !== ""
          ) {
            return candidate;
          }
        }

        return DEFAULT_CHAPTER_ID;
      };

      const destinationReferenceLesson =
        destinationLessonBeforeMove ||
        reorderedLessons[
          Math.min(destinationIndex, reorderedLessons.length - 1)
        ] ||
        movedLesson;

      setLessonsReordering(true);
      try {
        const chapterIdForPayload = resolveChapterId(sourceLesson);
        const payload = {
          course_id: toPayloadValue(selectedCourseId),
          module_id: toPayloadValue(selectedModuleId),
          chapter_id: toPayloadValue(chapterIdForPayload),
          source_lesson_id: toPayloadValue(resolveLessonId(sourceLesson)),
          dest_lesson_id: toPayloadValue(
            resolveLessonId(destinationReferenceLesson)
          ),
        };

        await LessonAuthoringService.rearrangeLesson(payload);
        const refreshedLessons = await LessonAuthoringService.fetchLessons(
          selectedCourseId,
          selectedModuleId
        );
        const sortedLessons = sortLessonsBySequence(refreshedLessons);
        setLessons(sortedLessons);
        setSelectedLesson((prev) => {
          if (!prev) {
            return prev;
          }

          const prevIdentifier =
            prev?.id ?? prev?.lessonId ?? prev?.LessonId ?? prev?.name;

          const updatedMatch = sortedLessons.find((lesson) => {
            const lessonIdentifier =
              lesson?.id ??
              lesson?.lessonId ??
              lesson?.LessonId ??
              lesson?.name;
            return String(lessonIdentifier) === String(prevIdentifier);
          });

          return updatedMatch || prev;
        });
      } catch (error) {
        console.error("Failed to rearrange lessons", error);
        setLessons(previousLessons);
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update the lesson order.",
          severity: "error",
        });
      } finally {
        setLessonsReordering(false);
      }
    },
    [
      lessons,
      selectedCourseId,
      selectedModuleId,
      setSelectedLesson,
      setSnackbar,
    ]
  );

  const handleLessonContentDraftChange = (field) => (eventOrValue) => {
    const value =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value ?? "";
    setNewLessonContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleCloseLessonFlow = () => {
    setLessonFlowOpen(false);
    setLessonFlowStep(0);
    setLessonFlowError(null);
    setNewLessonContent({ Name: "", Title: "", Value: "" });
    setLessonFormCategoryDraft({
      category: "",
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    });
  };

  const handleNextLessonFlowStep = () => {
    const currentStep = lessonFlowSteps[lessonFlowStep];

    if (currentStep?.key === "type") {
      const selectedType = matchLessonTypeOption(lessonForm.lessonType);
      if (!selectedType) {
        setLessonFlowError("Choose a lesson type to continue.");
        return;
      }
    }

    if (currentStep?.key === "details") {
      if (!lessonForm.name.trim()) {
        setLessonFlowError("Lesson name is required.");
        return;
      }

      if (!lessonForm.title.trim()) {
        setLessonFlowError("Lesson title is required.");
        return;
      }
    }

    setLessonFlowError(null);
    setLessonFlowStep((prev) => Math.min(prev + 1, lessonFlowSteps.length - 1));
  };

  const handleBackLessonFlowStep = () => {
    setLessonFlowStep((prev) => Math.max(prev - 1, 0));
    setLessonFlowError(null);
  };

  const handleSubmitLessonFlow = async () => {
    if (!selectedCourseId || !selectedModuleId) {
      setLessonFlowError(
        "Please select a course and module before creating a lesson."
      );
      return;
    }

    const trimmedLessonName = lessonForm.name.trim();
    const trimmedLessonTitle = lessonForm.title.trim();

    if (!trimmedLessonName) {
      setLessonFlowError("Lesson name is required.");
      return;
    }

    if (!trimmedLessonTitle) {
      setLessonFlowError("Lesson title is required.");
      return;
    }

    const matchedLessonType =
      matchLessonTypeOption(lessonForm.lessonType) || DEFAULT_LESSON_TYPE;
    const normalizedCategories = formatCategoriesForPayload(
      lessonForm.categories
    );
    const totalCategoryEntries = Array.isArray(lessonForm.categories)
      ? lessonForm.categories.length
      : 0;
    const isAssessmentLesson = isAssessmentLessonTypeValue(
      matchedLessonType.value
    );
    const assessmentSettings = isAssessmentLesson
      ? formatAssessmentSettingsPayload(lessonForm.categories)
      : null;

    if (isAssessmentLesson) {
      if (!normalizedCategories.length || !totalCategoryEntries) {
        setLessonFlowError(
          "Add at least one category, skill type, and question total before saving."
        );
        return;
      }

      if (
        normalizedCategories.length !== totalCategoryEntries ||
        hasInvalidAssessmentEntries(lessonForm.categories)
      ) {
        setLessonFlowError(
          "Each assessment category must include a skill type and a positive number of questions."
        );
        return;
      }

      if (!assessmentSettings) {
        setLessonFlowError(
          "Unable to build assessment settings. Please review the entered categories and question totals."
        );
        return;
      }
    } else if (
      !normalizedCategories.length ||
      normalizedCategories.length !== totalCategoryEntries
    ) {
      setLessonFlowError(
        "Add at least one category and choose a skill type for each category before saving."
      );
      return;
    }

    const primaryCategory = getPrimaryCategoryFromEntries(normalizedCategories);

    const payload = {
      name: trimmedLessonName,
      duration: lessonForm.duration,
      grades: lessonForm.grades,
      seq: lessonForm.seq,
      title: trimmedLessonTitle,
      description: lessonForm.description,
      contentType: lessonForm.contentType,
      isAssignment: Boolean(lessonForm.isAssignment),
      openForStudent: Boolean(lessonForm.openForStudent),
      contentUri: {
        [DEFAULT_CHAPTER_ID]: (
          lessonForm.contentUri || trimmedLessonName
        ).trim(),
      },
      lessonType: matchedLessonType.value,
      category: primaryCategory.category,
      skillType: primaryCategory.skilltype,
      categories: isAssessmentLesson ? [] : normalizedCategories,
      settings: assessmentSettings || undefined,
    };

    setLessonFlowSaving(true);
    setLessonFlowError(null);
    try {
      await LessonAuthoringService.createLesson(
        selectedCourseId,
        selectedModuleId,
        DEFAULT_CHAPTER_ID,
        payload
      );
      setLessonFlowOpen(false);
      setLessonFlowStep(0);
      setLessonFlowError(null);
      setNewLessonContent({ Name: "", Title: "", Value: "" });
      setSnackbar({
        open: true,
        message: "Lesson created successfully.",
        severity: "success",
      });
      const refreshedLessons = await LessonAuthoringService.fetchLessons(
        selectedCourseId,
        selectedModuleId
      );
      const sortedRefreshedLessons = sortLessonsBySequence(refreshedLessons);
      setLessons(sortedRefreshedLessons);
      if (
        newLessonContent.Value ||
        newLessonContent.Title ||
        newLessonContent.Name
      ) {
        const matchingLesson = sortedRefreshedLessons.find(
          (lesson) => String(lesson.name).trim() === trimmedLessonName
        );
        if (matchingLesson) {
          try {
            const matchingLessonId = resolveLessonId(matchingLesson);
            const initialContentForm = {
              Name: newLessonContent.Name || trimmedLessonName,
              Title: newLessonContent.Title || trimmedLessonTitle,
              Value: newLessonContent.Value,
            };
            const initialContentPayload = buildLessonContentUpdatePayload({
              courseId: selectedCourseId,
              moduleId: selectedModuleId,
              chapterId: matchingLesson?.chapterId || DEFAULT_CHAPTER_ID,
              lesson: matchingLesson,
              contentForm: initialContentForm,
            });
            if (!matchingLessonId) {
              throw new Error("Missing lesson identifier for content update.");
            }
            await LessonAuthoringService.updateLessonContent(
              selectedCourseId,
              selectedModuleId,
              matchingLesson?.chapterId || DEFAULT_CHAPTER_ID,
              matchingLessonId,
              initialContentPayload,
              initialContentPayload?.pageNumber
            );
          } catch (error) {
            console.error(
              "Failed to save lesson content during creation",
              error
            );
            setSnackbar({
              open: true,
              message:
                "Lesson created, but we could not save the initial content.",
              severity: "warning",
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to create lesson", error);
      setLessonFlowError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to create the lesson."
      );
    } finally {
      setLessonFlowSaving(false);
    }
  };

  const handleContentFormChange = (field) => (eventOrValue) => {
    const value =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value ?? "";
    setContentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenLessonMetadataDialog = (lesson) => {
    if (!lesson) {
      return;
    }

    setLessonMetadataTarget(lesson);
    const matchedLessonType =
      matchLessonTypeOption(
        lesson?.lessonType ||
          lesson?.type ||
          lesson?.contentType ||
          lesson?.ContentType ||
          lesson?.category ||
          lesson?.skillType ||
          ""
      ) || DEFAULT_LESSON_TYPE;
    const lessonSequenceValue = getLessonSequenceValue(lesson);
    const fallbackCategory = normalizeCategoryValue(lesson?.category || "");
    const fallbackSkillType =
      normalizeCategoryValue(lesson?.skillType || lesson?.skilltype) ||
      DEFAULT_SKILL_TYPE;
    const sanitizedCategories = sanitizeCategoryEntries(lesson?.categories);
    const settingsEntries = extractAssessmentEntriesFromSettings(
      lesson?.settings
    );
    const isAssessmentLesson = isAssessmentLessonTypeValue(
      matchedLessonType.value
    );
    const baseCategories = isAssessmentLesson
      ? settingsEntries.length
        ? settingsEntries
        : sanitizedCategories
      : sanitizedCategories;
    const categories = baseCategories.length
      ? baseCategories
      : fallbackCategory
      ? [
          {
            category: fallbackCategory,
            skilltype: fallbackSkillType || DEFAULT_SKILL_TYPE,
            ...(isAssessmentLesson ? { questions: "" } : {}),
          },
        ]
      : [];
    setLessonMetadataForm({
      name: lesson?.name || "",
      title: lesson?.title || lesson?.lessonTitle || "",
      description: lesson?.description || lesson?.Description || "",
      duration:
        lesson?.duration === undefined || lesson?.duration === null
          ? ""
          : String(lesson.duration),
      grades: lesson?.grades || lesson?.Grades || "",
      seq:
        lessonSequenceValue === null || lessonSequenceValue === undefined
          ? ""
          : String(lessonSequenceValue),
      lessonType: matchedLessonType.value,
      categories,
      isAssignment: Boolean(
        lesson?.isAssignment ?? lesson?.IsAssignment ?? lesson?.assignment
      ),
      openForStudent: Boolean(lesson?.openForStudent),
    });
    setLessonMetadataCategoryDraft({
      category: "",
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    });
    setIsLessonMetadataDescriptionEditing(false);
    setLessonMetadataDialogOpen(true);
  };

  const handleCloseLessonMetadataDialog = () => {
    setLessonMetadataDialogOpen(false);
    setLessonMetadataTarget(null);
    setLessonMetadataForm({
      name: "",
      title: "",
      duration: "",
      grades: "",
      seq: "",
      description: "",
      lessonType: LESSON_TYPE_PLACEHOLDER.value,
      categories: [],
      isAssignment: false,
      openForStudent: false,
    });
    setLessonMetadataCategoryDraft({
      category: "",
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    });
    setIsLessonMetadataDescriptionEditing(false);
  };

  const handleLessonMetadataFormChange = (field) => (eventOrValue) => {
    const value =
      typeof eventOrValue === "object" &&
      eventOrValue !== null &&
      "target" in eventOrValue
        ? eventOrValue.target?.value ?? ""
        : eventOrValue ?? "";
    if (field === "lessonType") {
      const matchedType = matchLessonTypeOption(value) || DEFAULT_LESSON_TYPE;
      setLessonMetadataForm((prev) => {
        const previousType =
          matchLessonTypeOption(prev.lessonType) || DEFAULT_LESSON_TYPE;
        const existingCategories = Array.isArray(prev.categories)
          ? prev.categories.map((entry) => ({ ...entry }))
          : [];

        if (!existingCategories.length) {
          return {
            ...prev,
            lessonType: matchedType.value,
            categories: [],
          };
        }

        const updatedCategories = existingCategories.map((entry, index) => {
          if (index !== 0) {
            return entry;
          }

          const updatedEntry = { ...entry };

          if (!normalizeCategoryValue(updatedEntry.skilltype)) {
            updatedEntry.skilltype = DEFAULT_SKILL_TYPE;
          }

          if (
            normalizeCategoryValue(updatedEntry.category) === previousType.label
          ) {
            updatedEntry.category = "";
          }

          return updatedEntry;
        });

        return {
          ...prev,
          lessonType: matchedType.value,
          categories: updatedCategories,
        };
      });
      return;
    }

    setLessonMetadataForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLessonMetadataToggleChange = (field) => (event) => {
    const value = Boolean(event?.target?.checked);
    setLessonMetadataForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLessonMetadataCategoryDraftChange = (field) => (eventOrValue) => {
    const value =
      typeof eventOrValue === "object" &&
      eventOrValue !== null &&
      "target" in eventOrValue
        ? eventOrValue.target?.value ?? ""
        : eventOrValue ?? "";

    setLessonMetadataCategoryDraft((prev) => ({
      ...prev,
      [field]: typeof value === "string" ? value : String(value ?? ""),
    }));
  };

  const handleAddLessonMetadataCategory = () => {
    const normalizedCategory = normalizeCategoryValue(
      lessonMetadataCategoryDraft.category
    );
    const normalizedSkilltype =
      normalizeCategoryValue(lessonMetadataCategoryDraft.skilltype) ||
      DEFAULT_SKILL_TYPE;
    const normalizedQuestions = normalizeQuestionValue(
      lessonMetadataCategoryDraft.questions
    );
    const numericQuestions = normalizeNumericValue(normalizedQuestions);
    const isAssessmentLesson = isAssessmentLessonTypeValue(
      lessonMetadataForm.lessonType
    );

    if (!normalizedCategory || !normalizedSkilltype) {
      return;
    }

    if (
      isAssessmentLesson &&
      (normalizedQuestions === "" ||
        numericQuestions === undefined ||
        !Number.isFinite(numericQuestions) ||
        numericQuestions <= 0)
    ) {
      return;
    }

    setLessonMetadataForm((prev) => {
      const categories = Array.isArray(prev.categories)
        ? prev.categories.map((entry) => ({ ...entry }))
        : [];

      if (
        isCategoryCombinationDuplicate(
          categories,
          normalizedCategory,
          normalizedSkilltype
        )
      ) {
        return prev;
      }

      return {
        ...prev,
        categories: [
          ...categories,
          {
            category: normalizedCategory,
            skilltype: normalizedSkilltype,
            ...(isAssessmentLesson
              ? { questions: String(Number(numericQuestions)) }
              : {}),
          },
        ],
      };
    });

    setLessonMetadataCategoryDraft((prev) => ({
      category: prev.category,
      skilltype: DEFAULT_SKILL_TYPE,
      questions: "",
    }));
  };

  const handleRemoveLessonMetadataCategory = (index) => () => {
    setLessonMetadataForm((prev) => {
      const categories = Array.isArray(prev.categories)
        ? prev.categories.filter((_, itemIndex) => itemIndex !== index)
        : [];

      return { ...prev, categories };
    });
  };

  const handleSaveLessonMetadata = async () => {
    if (!lessonMetadataTarget || savingLessonMetadata) {
      return;
    }

    const normalizedCategories = formatCategoriesForPayload(
      lessonMetadataForm.categories
    );
    const totalCategoryEntries = Array.isArray(lessonMetadataForm.categories)
      ? lessonMetadataForm.categories.length
      : 0;
    const matchedLessonType =
      matchLessonTypeOption(lessonMetadataForm.lessonType) ||
      DEFAULT_LESSON_TYPE;
    const isAssessmentLesson = isAssessmentLessonTypeValue(
      matchedLessonType.value
    );
    const assessmentSettings = isAssessmentLesson
      ? formatAssessmentSettingsPayload(lessonMetadataForm.categories)
      : null;

    if (isAssessmentLesson) {
      if (!normalizedCategories.length || !totalCategoryEntries) {
        setSnackbar({
          open: true,
          message:
            "Add at least one category, skill type, and question total before saving.",
          severity: "error",
        });
        return;
      }

      if (
        normalizedCategories.length !== totalCategoryEntries ||
        hasInvalidAssessmentEntries(lessonMetadataForm.categories)
      ) {
        setSnackbar({
          open: true,
          message:
            "Each assessment category must include a skill type and a positive number of questions.",
          severity: "error",
        });
        return;
      }

      if (!assessmentSettings) {
        setSnackbar({
          open: true,
          message:
            "Unable to build assessment settings. Please review the entered categories and question totals.",
          severity: "error",
        });
        return;
      }
    } else if (
      !normalizedCategories.length ||
      normalizedCategories.length !== totalCategoryEntries
    ) {
      setSnackbar({
        open: true,
        message:
          "Each selected category must include a corresponding skill type before saving.",
        severity: "error",
      });
      return;
    }

    if (selectedCourseId === undefined || selectedCourseId === null) {
      setSnackbar({
        open: true,
        message: "Select a course before updating lesson metadata.",
        severity: "error",
      });
      return;
    }

    if (selectedModuleId === undefined || selectedModuleId === null) {
      setSnackbar({
        open: true,
        message: "Select a module before updating lesson metadata.",
        severity: "error",
      });
      return;
    }

    const targetIdentifier = resolveLessonId(lessonMetadataTarget);

    if (!targetIdentifier) {
      setSnackbar({
        open: true,
        message:
          "Unable to determine which lesson to update. Please refresh and try again.",
        severity: "error",
      });
      return;
    }

    const primaryCategory = getPrimaryCategoryFromEntries(normalizedCategories);
    const normalizedSeqNumeric = normalizeNumericValue(lessonMetadataForm.seq);
    const seqValue =
      normalizedSeqNumeric === undefined
        ? lessonMetadataForm.seq === undefined ||
          lessonMetadataForm.seq === null ||
          lessonMetadataForm.seq === ""
          ? ""
          : lessonMetadataForm.seq
        : normalizedSeqNumeric;
    const updatedLesson = {
      ...lessonMetadataTarget,
      name: lessonMetadataForm.name,
      title: lessonMetadataForm.title,
      lessonTitle:
        lessonMetadataForm.title || lessonMetadataTarget?.lessonTitle,
      duration:
        lessonMetadataForm.duration === ""
          ? ""
          : Number(lessonMetadataForm.duration),
      Duration:
        lessonMetadataForm.duration === ""
          ? ""
          : Number(lessonMetadataForm.duration),
      grades: lessonMetadataForm.grades,
      Grades: lessonMetadataForm.grades,
      lessonType: lessonMetadataForm.lessonType,
      type: lessonMetadataForm.lessonType || lessonMetadataTarget?.type,
      description: lessonMetadataForm.description,
      Description: lessonMetadataForm.description,
      isAssignment: Boolean(lessonMetadataForm.isAssignment),
      contentType:
        lessonMetadataForm.lessonType || lessonMetadataTarget?.contentType,
      ContentType:
        lessonMetadataForm.lessonType || lessonMetadataTarget?.ContentType,
      category: primaryCategory.category,
      skillType: primaryCategory.skilltype,
      skilltype: primaryCategory.skilltype,
      categories: isAssessmentLesson ? [] : normalizedCategories,
      openForStudent: Boolean(lessonMetadataForm.openForStudent),
      seq: seqValue,
      sequence: seqValue,
      Sequence: seqValue,
      Seq: seqValue,
      settings: isAssessmentLesson ? assessmentSettings : undefined,
    };

    const lessonIdentifier = targetIdentifier;
    const payload = buildLessonContentUpdatePayload({
      courseId: selectedCourseId,
      moduleId: selectedModuleId,
      chapterId: lessonMetadataTarget?.chapterId || DEFAULT_CHAPTER_ID,
      lesson: lessonMetadataTarget,
      contentForm: {
        Name: lessonMetadataForm.name,
        Title: lessonMetadataForm.title,
        description: lessonMetadataForm.description,
        duration: lessonMetadataForm.duration,
        seq: lessonMetadataForm.seq,
        lessonType: lessonMetadataForm.lessonType,
        contentType: lessonMetadataForm.lessonType,
        categories: lessonMetadataForm.categories,
        isAssignment: lessonMetadataForm.isAssignment,
        openForStudent: lessonMetadataForm.openForStudent,
        grades: lessonMetadataForm.grades,
      },
    });
    payload.categories = isAssessmentLesson ? undefined : normalizedCategories;
    payload.category = primaryCategory.category;
    payload.skillType = primaryCategory.skilltype;
    payload.grades = lessonMetadataForm.grades;
    payload.skilltype = primaryCategory.skilltype;
    payload.settings = assessmentSettings || undefined;

    if (normalizedSeqNumeric !== undefined) {
      payload.seq = normalizedSeqNumeric;
      payload.sequence = normalizedSeqNumeric;
      payload.Sequence = normalizedSeqNumeric;
    }

    setSavingLessonMetadata(true);
    try {
      await LessonAuthoringService.updateLessonContent(
        selectedCourseId,
        selectedModuleId,
        lessonMetadataTarget?.chapterId || DEFAULT_CHAPTER_ID,
        lessonIdentifier,
        payload,
        payload?.pageNumber
      );

      setLessons((prev) => {
        if (!Array.isArray(prev)) {
          return prev;
        }

        const updatedLessons = prev.map((lesson) => {
          const lessonId = resolveLessonId(lesson);
          if (String(lessonId) === String(targetIdentifier)) {
            return { ...lesson, ...updatedLesson };
          }
          return lesson;
        });

        return sortLessonsBySequence(updatedLessons);
      });

      setSelectedLesson((prev) => {
        if (!prev) {
          return prev;
        }

        const prevIdentifier = resolveLessonId(prev);
        if (String(prevIdentifier) === String(targetIdentifier)) {
          return { ...prev, ...updatedLesson };
        }

        return prev;
      });

      setContentForm((prev) => {
        if (!prev || !selectedLesson) {
          return prev;
        }

        const selectedIdentifier = resolveLessonId(selectedLesson);

        if (String(selectedIdentifier) === String(targetIdentifier)) {
          return {
            ...prev,
            Title: lessonMetadataForm.title || prev.Title,
            Name: lessonMetadataForm.name || prev.Name,
          };
        }

        return prev;
      });

      setLessonMetadataTarget((prev) => {
        if (!prev) {
          return prev;
        }

        const prevIdentifier = resolveLessonId(prev);
        if (String(prevIdentifier) === String(targetIdentifier)) {
          return { ...prev, ...updatedLesson };
        }

        return prev;
      });

      setSnackbar({
        open: true,
        message: "Lesson metadata updated.",
        severity: "success",
      });

      handleCloseLessonMetadataDialog();
    } catch (error) {
      console.error("Failed to update lesson metadata", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update the lesson metadata.",
        severity: "error",
      });
    } finally {
      setSavingLessonMetadata(false);
    }
  };

  const handleOpenCourseDialog = (course = null) => {
    if (course) {
      setCourseForm({
        id: course.id ?? "",
        name: course.name || "",
        description: course.description || "",
        mediaUrl: course.mediaUrl || "",
        startDate: course.startDate
          ? String(course.startDate).slice(0, 10)
          : "",
        active: course.active !== undefined ? Boolean(course.active) : true,
        locked: course.locked || "no",
        continueOnReconnect:
          course?.settings?.Game?.ContinueOnReconnect !== undefined
            ? Boolean(course.settings.Game.ContinueOnReconnect)
            : true,
        maxTimeBeforeReset:
          course?.settings?.Game?.MaxTimeInMinBeforeGameReset ?? 12000,
      });
    } else {
      setCourseForm({ ...defaultCourseForm });
    }
    setCourseDialogError(null);
    setCourseDialogOpen(true);
  };

  const handleCloseCourseDialog = () => {
    setCourseDialogOpen(false);
    setCourseForm({ ...defaultCourseForm });
    setCourseDialogError(null);
  };

  const handleCourseFormChange = (field) => (event) => {
    setCourseForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCourseBooleanChange = (field) => (event) => {
    setCourseForm((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSubmitCourse = async () => {
    const trimmedName = courseForm.name.trim();
    const trimmedDescription = courseForm.description.trim();
    const trimmedMediaUrl = courseForm.mediaUrl.trim();
    const trimmedStartDate = courseForm.startDate
      ? courseForm.startDate.trim()
      : "";
    const payload = {
      name: trimmedName,
      description: trimmedDescription,
      mediaUrl: trimmedMediaUrl,
      startDate: trimmedStartDate,
      active: Boolean(courseForm.active),
      locked: courseForm.locked || "no",
      settings: {
        Game: {
          ContinueOnReconnect: Boolean(courseForm.continueOnReconnect),
          MaxTimeInMinBeforeGameReset:
            Number(courseForm.maxTimeBeforeReset) || 0,
        },
      },
    };

    if (!payload.name) {
      setCourseDialogError("Course name is required.");
      return;
    }

    if (!payload.startDate) {
      setCourseDialogError("Please provide a course start date.");
      return;
    }

    setSavingCourse(true);
    setCourseDialogError(null);

    try {
      if (courseForm.id) {
        await LessonAuthoringService.updateCourse(courseForm.id, payload);
        setSnackbar({
          open: true,
          message: "Course updated successfully.",
          severity: "success",
        });
      } else {
        await LessonAuthoringService.createCourse(payload);
        setSnackbar({
          open: true,
          message: "Course created successfully.",
          severity: "success",
        });
      }

      setCourseDialogOpen(false);
      setCourseForm({ ...defaultCourseForm });
      await handleRefreshCourses();
    } catch (error) {
      console.error("Failed to save course", error);
      setCourseDialogError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save the course."
      );
    } finally {
      setSavingCourse(false);
    }
  };

  const handleSaveContent = async () => {
    if (!selectedCourseId || !selectedModuleId || !selectedLesson) {
      return;
    }

    const lessonIdentifier = resolveLessonId(selectedLesson);

    if (!lessonIdentifier) {
      setLessonContentError(
        "Unable to determine which lesson to update. Please refresh and try again."
      );
      return;
    }

    const payload = buildLessonContentUpdatePayload({
      courseId: selectedCourseId,
      moduleId: selectedModuleId,
      chapterId: selectedLesson?.chapterId || DEFAULT_CHAPTER_ID,
      lesson: selectedLesson,
      contentForm,
    });

    // ✅ CRITICAL: always send page info
    payload.pageNumber = currentPage;
    payload.PageNumber = currentPage; // backend safety

    // 🔍 Debug (can remove later)
    console.log("▶️ handleSaveContent called");
    console.log("Lesson ID:", lessonIdentifier);
    console.log("Saving Page Number:", currentPage);
    console.log("Content length:", payload.Value?.length);

    setSavingContent(true);
    setLessonContentError(null);

    try {
      // 🔥 IMPORTANT: CAPTURE RESPONSE
      const data = await LessonAuthoringService.updateLessonContent(
        selectedCourseId,
        selectedModuleId,
        selectedLesson?.chapterId || DEFAULT_CHAPTER_ID,
        lessonIdentifier,
        payload,
        currentPage
      );

      // 🔥 SYNC FRONTEND WITH BACKEND
      setLessonContent(data || null);
      setCurrentPage(data?.CurPgNum ?? currentPage);
      setTotalPages(data?.TotalPages ?? totalPages);

      setContentForm({
        Name: data?.Name ?? contentForm.Name,
        Title: data?.Title ?? contentForm.Title,
        Value: data?.Value ?? contentForm.Value,
      });

      setSnackbar({
        open: true,
        message: "Lesson content saved successfully.",
        severity: "success",
      });

      // 🔁 Update lesson metadata list (NOT page content)
      setLessons((prev) =>
        Array.isArray(prev)
          ? prev.map((lesson) => {
              const lessonId = resolveLessonId(lesson);
              if (String(lessonId) === String(lessonIdentifier)) {
                return {
                  ...lesson,
                  name: data?.Name ?? lesson.name,
                  title: data?.Title ?? lesson.title,
                  lessonTitle: data?.Title ?? lesson.lessonTitle,
                  description:
                    payload.description ??
                    lesson.description ??
                    lesson.Description,
                  duration:
                    payload.duration ?? lesson.duration ?? lesson.Duration,
                  categories: payload.categories ?? lesson.categories,
                  category: payload.category ?? lesson.category,
                  skillType: payload.skillType ?? lesson.skillType,
                  skilltype: payload.skilltype ?? lesson.skilltype,
                  contentType: payload.contentType ?? lesson.contentType,
                  lessonType: payload.lessonType ?? lesson.lessonType,
                  openForStudent:
                    payload.openForStudent ?? lesson.openForStudent,
                  isAssignment:
                    payload.isAssignment ??
                    lesson.isAssignment ??
                    lesson.IsAssignment,
                };
              }
              return lesson;
            })
          : prev
      );

      setSelectedLesson((prev) => {
        if (!prev) return prev;
        const prevIdentifier = resolveLessonId(prev);
        if (String(prevIdentifier) !== String(lessonIdentifier)) return prev;

        return {
          ...prev,
          name: data?.Name ?? prev.name,
          title: data?.Title ?? prev.title,
          lessonTitle: data?.Title ?? prev.lessonTitle,
          description: payload.description ?? prev.description,
          duration: payload.duration ?? prev.duration,
          categories: payload.categories ?? prev.categories,
          category: payload.category ?? prev.category,
          skillType: payload.skillType ?? prev.skillType,
          skilltype: payload.skilltype ?? prev.skilltype,
          contentType: payload.contentType ?? prev.contentType,
          lessonType: payload.lessonType ?? prev.lessonType,
          openForStudent: payload.openForStudent ?? prev.openForStudent,
          isAssignment:
            payload.isAssignment ?? prev.isAssignment ?? prev.IsAssignment,
        };
      });
    } catch (error) {
      console.error("Failed to save lesson content", error);
      setLessonContentError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save the lesson content."
      );
    } finally {
      setSavingContent(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const selectedCourse = useMemo(
    () => instructorCourses.find((course) => course.id === selectedCourseId),
    [instructorCourses, selectedCourseId]
  );

  const selectedModule = useMemo(() => {
    if (!selectedModuleId) {
      return null;
    }

    return (
      modules.find((module) => {
        const moduleIdentifier = module?.id ?? module?.moduleId;
        return (
          moduleIdentifier !== undefined &&
          moduleIdentifier !== null &&
          String(moduleIdentifier) === String(selectedModuleId)
        );
      }) || null
    );
  }, [modules, selectedModuleId]);

  const selectedLessonTypeInfo = useMemo(
    () => (selectedLesson ? getLessonTypeForLesson(selectedLesson) : null),
    [selectedLesson]
  );

  const renderCourseList = (listProps = {}) => {
    if (coursesLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={30} />
        </Box>
      );
    }

    if (coursesError) {
      return <Alert severity="error">{coursesError}</Alert>;
    }

    if (instructorCourses.length === 0) {
      return (
        <Alert severity="info">
          No instructor courses were found for your account. Please contact an
          administrator if you believe this is incorrect.
        </Alert>
      );
    }

    const { sx: listSx, ...listRest } = listProps;

    return (
      <List
        dense
        disablePadding
        {...listRest}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          borderRadius: 2,
          ...listSx,
        }}
      >
        {instructorCourses.map((course) => {
          const isSelected = String(selectedCourseId) === String(course.id);
          const moduleCount = Number(
            course.moduleCount ??
              course.modulesCount ??
              course.totalModules ??
              course.modules?.length ??
              0
          );
          const lessonCount = Number(
            course.lessonCount ??
              course.totalLessons ??
              course.lessonsCount ??
              0
          );

          return (
            <ListItemButton
              key={course.id}
              selected={isSelected}
              onClick={() => handleSelectCourse(course.id)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
              }}
            >
              <Stack spacing={0.75} sx={{ width: "100%" }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600 }}
                      noWrap
                    >
                      {course.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {course.description || "No description provided."}
                    </Typography>
                  </Stack>
                  <Tooltip title="Edit course">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleOpenCourseDialog(course);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Chip
                    size="small"
                    label={`${moduleCount} modules`}
                    color="primary"
                    variant={isSelected ? "filled" : "outlined"}
                  />
                  <Chip
                    size="small"
                    label={`${lessonCount} lessons`}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                </Stack>
              </Stack>
            </ListItemButton>
          );
        })}
      </List>
    );
  };

  const renderCourseOverview = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
            >
              <SchoolOutlinedIcon fontSize="small" />
            </Avatar>
            <Stack spacing={0.25}>
              <Typography variant="overline" color="text.secondary">
                Courses
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Authoring library
              </Typography>
            </Stack>
          </Stack>
          <Tooltip title="Refresh courses">
            <span>
              <IconButton
                onClick={() => {
                  void handleRefreshCourses();
                }}
                disabled={coursesLoading}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Choose a course to view its detail panel. Use the navigation list to
          switch between courses you are actively managing.
        </Typography>
        {renderCourseList()}
      </Stack>
    </Paper>
  );

  const renderModulesTab = () => {
    if (!selectedCourse) {
      return (
        <Alert severity="info">
          Select a course to explore its modules and lessons.
        </Alert>
      );
    }

    const formatModuleDate = (value) => {
      if (!value) {
        return "—";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const getModulePercentLabel = (module) => {
      const rawValue =
        module?.percentCompleted ??
        module?.percentComplete ??
        module?.completionPercent ??
        module?.completionPercentage ??
        module?.progressPercentage ??
        module?.progressPercent ??
        module?.progress ??
        null;

      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return "—";
      }

      const numeric = Number(rawValue);

      if (Number.isFinite(numeric)) {
        const precision = Number.isInteger(numeric) ? 0 : 1;
        const formatted = Number(numeric.toFixed(precision));
        return `${formatted}%`;
      }

      return String(rawValue);
    };

    return (
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                  Modules
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Plan the learning journey
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review every module in this course. Click a row to drill into
                  its lessons or use the pencil icon to update module
                  information.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModuleDialog()}
                >
                  New module
                </Button>
              </Stack>
            </Stack>
            {modulesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : modulesError ? (
              <Alert severity="error">{modulesError}</Alert>
            ) : modules.length === 0 ? (
              <Alert severity="info">
                No modules yet. Create your first module to begin planning the
                experience.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="course modules table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Scheduled Date</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell align="right">Percent Completed</TableCell>
                      <TableCell>Completed Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modules.map((module) => {
                      const moduleIdentifier = module?.id ?? module?.moduleId;
                      const isSelected =
                        moduleIdentifier !== undefined &&
                        moduleIdentifier !== null &&
                        String(moduleIdentifier) === String(selectedModuleId);
                      const durationLabel = module?.duration
                        ? `${module.duration} min`
                        : "—";
                      const scheduledDateLabel = formatModuleDate(
                        module?.scheduledDate ??
                          module?.scheduleDate ??
                          module?.plannedDate
                      );
                      const percentCompletedLabel =
                        getModulePercentLabel(module);
                      const dateCompletedLabel = formatModuleDate(
                        module?.dateCompleted ??
                          module?.completedDate ??
                          module?.completionDate ??
                          module?.completedOn
                      );

                      return (
                        <TableRow
                          key={moduleIdentifier || module?.name}
                          hover
                          selected={isSelected}
                          onClick={() => handleSelectModule(module)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell component="th" scope="row">
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                              noWrap
                            >
                              {module?.name || moduleIdentifier}
                            </Typography>
                          </TableCell>
                          <TableCell>{scheduledDateLabel}</TableCell>
                          <TableCell align="right">{durationLabel}</TableCell>
                          <TableCell align="right">
                            {percentCompletedLabel}
                          </TableCell>
                          <TableCell>{dateCompletedLabel}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit module">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleOpenModuleDialog(module);
                                  }}
                                >
                                  <SettingsOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderCourseSettingsTab = () => {
    if (!selectedCourse) {
      return null;
    }

    const courseSettings = [
      {
        label: "Start date",
        value: selectedCourse.startDate || "Not scheduled",
      },
      { label: "Join code", value: selectedCourse.joinCode || "—" },
      {
        label: "Status",
        value: selectedCourse.active === false ? "Draft" : "Active",
      },
      {
        label: "Locked",
        value: selectedCourse.locked === "yes" ? "Locked" : "Unlocked",
      },
    ];

    const gameSettings = selectedCourse?.settings?.Game || null;

    return (
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Course settings
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleOpenCourseDialog(selectedCourse)}
              >
                Edit course
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {courseSettings.map((item) => (
                <Grid key={item.label} item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: 1,
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {gameSettings && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Game setting</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Continue on reconnect</TableCell>
                      <TableCell>
                        {gameSettings.ContinueOnReconnect
                          ? "Enabled"
                          : "Disabled"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Max time before reset (minutes)</TableCell>
                      <TableCell>
                        {gameSettings.MaxTimeInMinBeforeGameReset ?? "—"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderCourseAnalyticsTab = () => {
    if (!selectedCourse) {
      return null;
    }

    const moduleCount = modules.length;
    const totalLessons = lessons.length;
    const activeLessons = lessons.filter(
      (lesson) => lesson.openForStudent
    ).length;

    const analyticsCards = [
      { label: "Modules", value: moduleCount },
      { label: "Lessons in view", value: totalLessons },
      { label: "Visible to students", value: activeLessons },
    ];

    return (
      <Grid container spacing={3}>
        {analyticsCards.map((card) => (
          <Grid key={card.label} item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: 1,
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Engagement insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deeper analytics will surface here, including learner progress,
                completion rates, and content performance trends. For now, keep
                building lessons to unlock richer insights.
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderCourseDetail = () => {
    if (!selectedCourse) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Stack spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Select a course to get started
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a course from the overview above or create a new one to
              begin authoring modules and lessons.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCourseDialog()}
            >
              Create course
            </Button>
          </Stack>
        </Paper>
      );
    }

    const statusLabel = selectedCourse.active === false ? "Draft" : "Active";
    const moduleCount = Number(
      selectedCourse.moduleCount ??
        selectedCourse.modulesCount ??
        selectedCourse.totalModules ??
        selectedCourse.modules?.length ??
        0
    );
    const lessonCount = Number(
      selectedCourse.lessonCount ??
        selectedCourse.totalLessons ??
        selectedCourse.lessonsCount ??
        0
    );
    const enrollmentCount = Number(
      selectedCourse.learners ??
        selectedCourse.enrollment ??
        selectedCourse.totalEnrollment ??
        0
    );

    const renderCourseOverviewTab = () => (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <CardActionArea
            onClick={() => setCourseDetailTab("modules")}
            sx={{ borderRadius: 3, alignItems: "stretch" }}
          >
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack spacing={2}>
                <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary">
                    Course overview
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedCourse.name}
                  </Typography>
                  {selectedCourse.startDate && (
                    <Typography variant="body2" color="text.secondary">
                      Start date: {selectedCourse.startDate}
                    </Typography>
                  )}
                  <Typography variant="body1" color="text.secondary">
                    {selectedCourse.description || "No description provided."}
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, height: "100%" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Modules
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {moduleCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, height: "100%" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Lessons
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {lessonCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, height: "100%" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Learners
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {enrollmentCount || "—"}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </CardActionArea>
        </Paper>
      </Stack>
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">
                Selected course
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {selectedCourse.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage the structure, settings, and analytics for this course.
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
            >
              <Chip size="small" color="success" label={statusLabel} />
              {selectedCourse.joinCode && (
                <Chip
                  size="small"
                  color="info"
                  label={`Code ${selectedCourse.joinCode}`}
                />
              )}
            </Stack>
          </Stack>
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Box
              component="form"
              onSubmit={handleCourseSearchSubmit}
              noValidate
              sx={{ width: "100%" }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  value={courseSearchQuery}
                  onChange={(event) => setCourseSearchQuery(event.target.value)}
                  label="Search course content"
                  placeholder="Search lessons, modules, and resources"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (courseSearchQuery ||
                      courseSearchResults.length > 0 ||
                      courseSearchPerformed ||
                      courseSearchError) && (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleClearCourseSearch();
                          }}
                          aria-label="Clear course search"
                          size="small"
                          disabled={courseSearchLoading}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  label="Module filter"
                  value={courseSearchModuleId}
                  onChange={handleCourseSearchModuleChange}
                  size="small"
                  sx={{ minWidth: { xs: "100%", md: 220 } }}
                >
                  <MenuItem value="">All modules</MenuItem>
                  {modules.map((module) => {
                    const moduleIdentifier = module?.id ?? module?.moduleId;

                    if (
                      moduleIdentifier === undefined ||
                      moduleIdentifier === null
                    ) {
                      return null;
                    }

                    const moduleLabel =
                      module?.name ||
                      module?.title ||
                      `Module ${moduleIdentifier}`;

                    return (
                      <MenuItem
                        key={`course-search-module-${moduleIdentifier}`}
                        value={String(moduleIdentifier)}
                      >
                        {moduleLabel}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon fontSize="small" />}
                  disabled={courseSearchLoading}
                >
                  {courseSearchLoading ? "Searching…" : "Search"}
                </Button>
              </Stack>
            </Box>
            {courseSearchLoading && (
              <LinearProgress sx={{ borderRadius: 999, height: 3 }} />
            )}
            {courseSearchError && (
              <Alert severity="error">{courseSearchError}</Alert>
            )}
            {courseSearchPerformed &&
              !courseSearchLoading &&
              !courseSearchError &&
              courseSearchResults.length === 0 && (
                <Alert severity="info">
                  No course content matched your search.
                </Alert>
              )}
            {courseSearchResults.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ px: 2, py: 1.5, bgcolor: "background.default" }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Search results ({courseSearchResults.length})
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleClearCourseSearch}
                    disabled={courseSearchLoading}
                  >
                    Clear results
                  </Button>
                </Stack>
                <Divider />
                <List disablePadding sx={{ maxHeight: 360, overflowY: "auto" }}>
                  {courseSearchResults.map((result, index) => {
                    const itemKey =
                      result.key || `course-search-result-${index}`;

                    return (
                      <React.Fragment key={itemKey}>
                        <ListItem disablePadding alignItems="flex-start">
                          <ListItemButton
                            alignItems="flex-start"
                            onClick={() =>
                              handleCourseSearchResultClick(result)
                            }
                            selected={courseSearchActiveKey === result.key}
                          >
                            <Stack spacing={0.75} sx={{ width: "100%" }}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                              >
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  alignItems={{
                                    xs: "flex-start",
                                    sm: "center",
                                  }}
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {result.lessonTitle}
                                  </Typography>
                                  {result.moduleTitle && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={result.moduleTitle}
                                      sx={{ maxWidth: 200 }}
                                    />
                                  )}
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {result.type && (
                                    <Chip
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      label={result.type}
                                      sx={{ maxWidth: 160 }}
                                    />
                                  )}
                                  <ArrowForwardIcon
                                    fontSize="small"
                                    color="action"
                                  />
                                </Stack>
                              </Stack>
                              {result.snippet && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 3,
                                  }}
                                >
                                  {result.snippet}
                                </Typography>
                              )}
                            </Stack>
                          </ListItemButton>
                        </ListItem>
                        {index < courseSearchResults.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Paper>
            )}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Tabs
                value={courseDetailTab}
                onChange={(event, value) => setCourseDetailTab(value)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ flexGrow: 1 }}
              >
                <Tab
                  value="overview"
                  label="Overview"
                  icon={<ArticleOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                />
                <Tab
                  value="modules"
                  label="Modules"
                  icon={<ViewSidebarIcon fontSize="small" />}
                  iconPosition="start"
                />
                <Tab
                  value="settings"
                  label="Settings"
                  icon={<SettingsOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                />
                <Tab
                  value="analytics"
                  label="Analytics"
                  icon={<AnalyticsOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                />
              </Tabs>
              <Tooltip title="Edit course">
                <span>
                  <IconButton
                    onClick={() => handleOpenCourseDialog(selectedCourse)}
                    size="small"
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          {courseDetailTab === "overview" && renderCourseOverviewTab()}
          {courseDetailTab === "modules" && renderModulesTab()}
          {courseDetailTab === "settings" && renderCourseSettingsTab()}
          {courseDetailTab === "analytics" && renderCourseAnalyticsTab()}
        </Stack>
      </Paper>
    );
  };

  const renderModuleWorkspace = () => {
    if (!selectedModule) {
      return null;
    }

    const selectedModuleTitle =
      selectedModule?.name ||
      selectedModule?.title ||
      selectedModule?.moduleTitle ||
      selectedModule?.moduleId ||
      "Module";
    const selectedLessonIdentifier = selectedLesson
      ? selectedLesson?.id ??
        selectedLesson?.lessonId ??
        selectedLesson?.LessonId ??
        selectedLesson?.name
      : null;
    const selectedLessonTitle =
      stripHtmlTags(
        selectedLesson?.title ||
          selectedLesson?.name ||
          selectedLesson?.lessonTitle ||
          selectedLessonIdentifier ||
          "Lesson"
      ) || "Lesson";
    const selectedLessonDuration = selectedLesson?.duration
      ? `${selectedLesson.duration} min`
      : "—";
    const selectedLessonVisible = Boolean(selectedLesson?.openForStudent);
    const selectedLessonIsAssignment =
      Boolean(
        selectedLesson?.isAssignment ??
          selectedLesson?.IsAssignment ??
          contentForm?.isAssignment
      ) || isAssignmentLessonTypeInfo(selectedLessonTypeInfo);
    const isLessonEditorVisible =
      Boolean(selectedLesson) && isEditingLessonContent;
    const showLessonsTable = !isLessonEditorVisible;

    return (
      <Stack spacing={3}>
        <Breadcrumbs
          aria-label="lesson list navigation"
          sx={{ fontSize: "0.875rem" }}
        >
          <Link
            underline="hover"
            color="inherit"
            component="button"
            onClick={handleReturnToCourse}
            sx={{ fontSize: "inherit", textAlign: "left" }}
          >
            Course details
          </Link>
          {isLessonEditorVisible ? (
            <Link
              underline="hover"
              color="inherit"
              component="button"
              onClick={handleExitLessonEditor}
              sx={{ fontSize: "inherit", textAlign: "left" }}
            >
              {`${selectedModuleTitle} lessons`}
            </Link>
          ) : (
            <Typography color="text.primary">{`${selectedModuleTitle} lessons`}</Typography>
          )}
          {isLessonEditorVisible && (
            <Typography color="text.primary">{selectedLessonTitle}</Typography>
          )}
        </Breadcrumbs>
        {showLessonsTable && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Lessons
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenLessonFlow}
                >
                  Add lesson
                </Button>
              </Stack>
              {lessonsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : lessonsError ? (
                <Alert severity="error">{lessonsError}</Alert>
              ) : lessons.length === 0 ? (
                <Alert severity="info">
                  This module does not have any lessons yet. Use the Add lesson
                  button to create one.
                </Alert>
              ) : (
                <Box sx={{ position: "relative" }}>
                  {lessonsReordering && (
                    <LinearProgress
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        borderRadius: 1,
                      }}
                    />
                  )}
                  <TableContainer
                    sx={{
                      opacity: lessonsReordering ? 0.6 : 1,
                      pointerEvents: lessonsReordering ? "none" : "auto",
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <DragDropContext onDragEnd={handleLessonDragEnd}>
                      <Table size="small" aria-label="module lessons table">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                              Reorder
                            </TableCell>
                            <TableCell align="right">Seq</TableCell>
                            <TableCell>Lesson</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Duration</TableCell>
                            <TableCell align="right">Visibility</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <Droppable
                          droppableId="module-lessons"
                          direction="vertical"
                        >
                          {(droppableProvided) => (
                            <TableBody
                              ref={droppableProvided.innerRef}
                              {...droppableProvided.droppableProps}
                            >
                              {lessons.map((lesson, index) => {
                                const lessonIdentifier =
                                  lesson?.id ??
                                  lesson?.lessonId ??
                                  lesson?.LessonId ??
                                  lesson?.name;
                                const lessonTitle =
                                  stripHtmlTags(
                                    lesson?.title ||
                                      lesson?.name ||
                                      lesson?.lessonTitle ||
                                      lessonIdentifier ||
                                      "Lesson"
                                  ) || "Lesson";
                                const lessonTypeInfo =
                                  getLessonTypeForLesson(lesson);
                                const isSelected =
                                  selectedLessonIdentifier !== null &&
                                  String(lessonIdentifier) ===
                                    String(selectedLessonIdentifier);
                                const durationLabel = lesson?.duration
                                  ? `${lesson.duration} min`
                                  : "—";
                                const isVisible = Boolean(
                                  lesson?.openForStudent
                                );
                                const sequenceValue =
                                  getLessonSequenceValue(lesson);
                                const sequenceLabel =
                                  sequenceValue !== null &&
                                  sequenceValue !== undefined
                                    ? sequenceValue
                                    : "—";
                                const draggableId = String(
                                  lessonIdentifier || `lesson-${index}`
                                );

                                return (
                                  <Draggable
                                    key={draggableId}
                                    draggableId={draggableId}
                                    index={index}
                                    isDragDisabled={lessonsReordering}
                                  >
                                    {(provided, snapshot) => (
                                      <TableRow
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        hover
                                        selected={isSelected}
                                        onClick={() =>
                                          handleSelectLesson(lesson)
                                        }
                                        sx={{
                                          cursor: snapshot.isDragging
                                            ? "grabbing"
                                            : "pointer",
                                          bgcolor: snapshot.isDragging
                                            ? "action.hover"
                                            : undefined,
                                        }}
                                        style={provided.draggableProps.style}
                                      >
                                        <TableCell padding="checkbox">
                                          <Box
                                            {...provided.dragHandleProps}
                                            onClick={(event) =>
                                              event.stopPropagation()
                                            }
                                            onMouseDown={(event) =>
                                              event.stopPropagation()
                                            }
                                            onTouchStart={(event) =>
                                              event.stopPropagation()
                                            }
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "text.disabled",
                                              cursor: lessonsReordering
                                                ? "not-allowed"
                                                : snapshot.isDragging
                                                ? "grabbing"
                                                : "grab",
                                            }}
                                            aria-label="Reorder lesson"
                                          >
                                            <DragIndicatorIcon fontSize="small" />
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                          {sequenceLabel}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                          <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 600 }}
                                            noWrap
                                          >
                                            {lessonTitle}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {lessonTypeInfo?.label || "—"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          {durationLabel}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Chip
                                            size="small"
                                            label={
                                              isVisible ? "Visible" : "Hidden"
                                            }
                                            color={
                                              isVisible ? "success" : "default"
                                            }
                                            variant={
                                              isVisible ? "filled" : "outlined"
                                            }
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            justifyContent="flex-end"
                                          >
                                            <Tooltip title="Edit content">
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleSelectLesson(lesson, {
                                                      enterEditMode: true,
                                                    });
                                                  }}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip title="Edit metadata">
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleOpenLessonMetadataDialog(
                                                      lesson
                                                    );
                                                  }}
                                                >
                                                  <SettingsOutlinedIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {droppableProvided.placeholder}
                            </TableBody>
                          )}
                        </Droppable>
                      </Table>
                    </DragDropContext>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </Paper>
        )}

        {isLessonEditorVisible && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1.5}
              >
                <Stack spacing={0.5}>
                  <Typography variant="overline" color="text.secondary">
                    Editing lesson
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedLessonTitle}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedLessonTypeInfo?.label && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={selectedLessonTypeInfo.label}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Duration ${selectedLessonDuration}`}
                    />
                    <Chip
                      size="small"
                      color={selectedLessonVisible ? "success" : "default"}
                      variant={selectedLessonVisible ? "filled" : "outlined"}
                      label={
                        selectedLessonVisible ? "Visible to students" : "Hidden"
                      }
                    />
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    onClick={handleExitLessonEditor}
                    disabled={savingContent || lessonContentLoading}
                  >
                    Cancel
                  </Button>
                  {savingContent && <CircularProgress size={22} />}
                  <Button
                    variant="contained"
                    onClick={handleSaveContent}
                    disabled={savingContent || lessonContentLoading}
                  >
                    Save content
                  </Button>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <PageSelector
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePageChange}
                />
                {addingPage && <CircularProgress size={20} />}
              </Stack>

              {lessonContentLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <Stack spacing={2}>
                  {lessonContentError && (
                    <Alert severity="error">{lessonContentError}</Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Content name"
                        value={contentForm.Name}
                        onChange={handleContentFormChange("Name")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Content title"
                        value={contentForm.Title}
                        onChange={handleContentFormChange("Title")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  {selectedLessonIsAssignment ? (
                    <AssignmentAuthoringShell
                      initialSections={lessonContent?.sections || []}
                    />
                  ) : (
                    <Box
                      sx={{
                        "& .tox-tinymce": { borderRadius: 2 },
                        "& .tox-edit-area iframe": { minHeight: 600 },
                      }}
                    >
                      <LessonRichTextEditor
                        value={contentForm.Value}
                        onChange={(value) =>
                          handleContentFormChange("Value")(value)
                        }
                        minHeight={600}
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    );
  };

  const isCourseSelected = Boolean(selectedCourse);
  const isModuleWorkspaceVisible = Boolean(selectedModule);
  const leftColumnContent = !isCourseSelected ? renderCourseOverview() : null;
  const shouldShowSidebar = Boolean(leftColumnContent);
  const shouldShowNewCourseButton = !isModuleWorkspaceVisible;

  return (
    <Stack spacing={4} sx={{ position: "relative", pb: 8 }}>
      <Grid container spacing={3} alignItems="stretch">
        {shouldShowSidebar && (
          <Grid item xs={12} lg={4} xl={3}>
            {leftColumnContent}
          </Grid>
        )}
        <Grid
          item
          xs={12}
          lg={shouldShowSidebar ? 8 : 12}
          xl={shouldShowSidebar ? 9 : 12}
        >
          {isModuleWorkspaceVisible
            ? renderModuleWorkspace()
            : renderCourseDetail()}
        </Grid>
      </Grid>

      <Zoom in={shouldShowNewCourseButton} mountOnEnter unmountOnExit>
        <Fab
          color="primary"
          variant="extended"
          onClick={() => handleOpenCourseDialog()}
          sx={{
            position: { xs: "fixed", md: "fixed" },
            bottom: { xs: 24, md: 32 },
            right: { xs: 24, md: 32 },
            zIndex: 1200,
          }}
          aria-label="Create a course"
        >
          <AddIcon sx={{ mr: 1 }} />+ New Course
        </Fab>
      </Zoom>

      <Dialog
        open={courseDialogOpen}
        onClose={handleCloseCourseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {courseForm.id ? "Update course" : "Create course"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {courseDialogError && (
              <Alert severity="error">{courseDialogError}</Alert>
            )}
            <TextField
              label="Course name"
              value={courseForm.name}
              onChange={handleCourseFormChange("name")}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={courseForm.description}
              onChange={handleCourseFormChange("description")}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Media URL"
              value={courseForm.mediaUrl}
              onChange={handleCourseFormChange("mediaUrl")}
              fullWidth
            />
            <TextField
              label="Start date"
              type="date"
              value={courseForm.startDate}
              onChange={handleCourseFormChange("startDate")}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(courseForm.active)}
                  onChange={handleCourseBooleanChange("active")}
                />
              }
              label="Course is active"
            />
            <TextField
              select
              label="Locked"
              value={courseForm.locked}
              onChange={handleCourseFormChange("locked")}
              helperText="Control learner access to the course."
              fullWidth
            >
              <MenuItem value="no">Unlocked</MenuItem>
              <MenuItem value="yes">Locked</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(courseForm.continueOnReconnect)}
                  onChange={handleCourseBooleanChange("continueOnReconnect")}
                />
              }
              label="Continue game on reconnect"
            />
            <TextField
              label="Max time before game reset (minutes)"
              type="number"
              value={courseForm.maxTimeBeforeReset}
              onChange={handleCourseFormChange("maxTimeBeforeReset")}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCourseDialog} disabled={savingCourse}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCourse}
            variant="contained"
            disabled={savingCourse}
          >
            {savingCourse
              ? "Saving..."
              : courseForm.id
              ? "Update course"
              : "Create course"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={moduleDialogOpen}
        onClose={handleCloseModuleDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingModuleId ? "Update module" : "Create module"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {moduleDialogError && (
              <Alert severity="error">{moduleDialogError}</Alert>
            )}
            <TextField
              label="Module ID"
              value={moduleForm.moduleId}
              onChange={handleModuleFormChange("moduleId")}
              helperText="Provide a unique identifier for this module."
              fullWidth
              required
            />
            <TextField
              label="Display ID"
              value={moduleForm.id}
              onChange={handleModuleFormChange("id")}
              helperText="Optional. Defaults to the Module ID if left blank."
              fullWidth
            />
            <TextField
              label="Module name"
              value={moduleForm.name}
              onChange={handleModuleFormChange("name")}
              fullWidth
              required
            />
            <TextField
              label="Module title"
              value={moduleForm.title}
              onChange={handleModuleFormChange("title")}
              fullWidth
            />
            <TextField
              label="Objective"
              value={moduleForm.objective}
              onChange={handleModuleFormChange("objective")}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Description"
              value={moduleForm.description}
              onChange={handleModuleFormChange("description")}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Media URL"
              value={moduleForm.mediaUrl}
              onChange={handleModuleFormChange("mediaUrl")}
              fullWidth
            />
            <TextField
              label="Scheduled date"
              type="date"
              value={moduleForm.scheduledDate}
              onChange={handleModuleFormChange("scheduledDate")}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              value={moduleForm.duration}
              onChange={handleModuleFormChange("duration")}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModuleDialog} disabled={savingModule}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitModule}
            variant="contained"
            disabled={savingModule}
          >
            {savingModule
              ? "Saving..."
              : editingModuleId
              ? "Update module"
              : "Create module"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lessonMetadataDialogOpen}
        onClose={handleCloseLessonMetadataDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit lesson metadata</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Lesson name"
              value={lessonMetadataForm.name}
              onChange={handleLessonMetadataFormChange("name")}
              fullWidth
            />
            <TextField
              label="Lesson title"
              value={lessonMetadataForm.title}
              onChange={handleLessonMetadataFormChange("title")}
              fullWidth
            />
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Description
                </Typography>
                {isLessonMetadataDescriptionEditing ? (
                  <Tooltip title="Done">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setIsLessonMetadataDescriptionEditing(false)
                      }
                      aria-label="Finish editing lesson description"
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={hasLessonMetadataDescription ? "Edit" : "Add"}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        setIsLessonMetadataDescriptionEditing(true)
                      }
                      aria-label="Edit lesson description"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Box
                ref={lessonMetadataDescriptionContainerRef}
                sx={(theme) => ({
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  border: isLessonMetadataDescriptionEditing
                    ? "none"
                    : `1px solid ${theme.palette.divider}`,
                  minHeight: 180,
                  cursor: isLessonMetadataDescriptionEditing
                    ? "auto"
                    : "default",
                  px: isLessonMetadataDescriptionEditing ? 0 : 2,
                  py: isLessonMetadataDescriptionEditing ? 0 : 1.5,
                  bgcolor: isLessonMetadataDescriptionEditing
                    ? "transparent"
                    : theme.palette.grey[50],
                  transition: "border-color 120ms ease-in-out",
                  "& .tox-tinymce": { borderRadius: 8 },
                  "& .tox-edit-area iframe": { minHeight: 180 },
                })}
              >
                {isLessonMetadataDescriptionEditing ? (
                  <LessonRichTextEditor
                    value={lessonMetadataForm.description}
                    onChange={handleLessonMetadataFormChange("description")}
                    minHeight={180}
                  />
                ) : hasLessonMetadataDescription ? (
                  <Box
                    sx={{
                      typography: "body2",
                      color: "text.primary",
                      "& p:first-of-type": { mt: 0 },
                      "& p:last-of-type": { mb: 0 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizedLessonMetadataDescription,
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ m: 0 }}
                  >
                    Use the edit icon to add a lesson description
                  </Typography>
                )}
              </Box>
            </Stack>
            <Grid
              container
              rowSpacing={2}
              columnSpacing={{ xs: 0, sm: 2 }}
              sx={{ width: "100%", m: 0 }}
            >
              <Grid item xs={12} sm={6} sx={{ pl: 0 }}>
                <TextField
                  label="Lesson type"
                  value={lessonMetadataForm.lessonType}
                  onChange={handleLessonMetadataFormChange("lessonType")}
                  select
                  fullWidth
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return LESSON_TYPE_PLACEHOLDER.label;
                      }
                      const matched = matchLessonTypeOption(selected);
                      return matched
                        ? matched.label
                        : LESSON_TYPE_PLACEHOLDER.label;
                    },
                  }}
                >
                  {LESSON_TYPE_SELECT_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value || "lesson-type-placeholder"}
                      value={option.value}
                      disabled={!option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ pl: { xs: 0, sm: 2 } }}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  value={lessonMetadataForm.duration}
                  onChange={handleLessonMetadataFormChange("duration")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ pl: 0 }}>
                <TextField
                  label="Sequence"
                  type="number"
                  value={lessonMetadataForm.seq}
                  onChange={handleLessonMetadataFormChange("seq")}
                  fullWidth
                />
              </Grid>
              {isLessonMetadataAssessment && (
                <Grid item xs={12} sm={6} sx={{ pl: { xs: 0, sm: 2 } }}>
                  <TextField
                    label="Grades"
                    value={lessonMetadataForm.grades}
                    onChange={handleLessonMetadataFormChange("grades")}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ pl: 0 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {isLessonMetadataAssessment
                      ? "Categories, skill types & question totals"
                      : "Categories & skill types"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-end"
                    sx={{
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Category"
                      value={lessonMetadataCategoryDraft.category}
                      onChange={handleLessonMetadataCategoryDraftChange(
                        "category"
                      )}
                      select
                      fullWidth
                      sx={{
                        flexGrow: { xs: 1, sm: 0 },
                        flexShrink: 1,
                        flexBasis: { xs: "100%", sm: "40%" },
                        maxWidth: { xs: "100%", sm: "40%" },
                      }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          const normalizedValue =
                            normalizeCategoryValue(selected);

                          if (!normalizedValue) {
                            return (
                              <Typography color="text.disabled">
                                Select Category
                              </Typography>
                            );
                          }

                          return getCategoryLabel(normalizedValue);
                        },
                      }}
                      helperText={taxonomyHelperText}
                      disabled={taxonomyLoading && !taxonomyOptions.length}
                    >
                      {(() => {
                        const categoryOptions = createCategoryOptions(
                          taxonomyOptions,
                          lessonMetadataCategoryDraft.category
                        );

                        if (categoryOptions.length) {
                          return categoryOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ));
                        }

                        return (
                          <MenuItem value="" disabled>
                            {taxonomyLoading
                              ? "Loading categories…"
                              : "No categories available"}
                          </MenuItem>
                        );
                      })()}
                    </TextField>
                    <TextField
                      label="Skill type"
                      value={lessonMetadataCategoryDraft.skilltype}
                      onChange={handleLessonMetadataCategoryDraftChange(
                        "skilltype"
                      )}
                      select
                      fullWidth
                      sx={{
                        flexBasis: { xs: "100%", sm: "40%" },
                        flexGrow: { xs: 1, sm: 0 },
                        flexShrink: 1,
                        maxWidth: { xs: "100%", sm: "40%" },
                      }}
                    >
                      {createSkillOptions(
                        lessonMetadataCategoryDraft.skilltype
                      ).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {isLessonMetadataAssessment && (
                      <TextField
                        label="Questions"
                        type="number"
                        value={lessonMetadataCategoryDraft.questions}
                        onChange={handleLessonMetadataCategoryDraftChange(
                          "questions"
                        )}
                        fullWidth
                        sx={{
                          flexBasis: { xs: "100%", sm: "20%" },
                          flexGrow: { xs: 1, sm: 0 },
                          flexShrink: 1,
                          maxWidth: { xs: "100%", sm: "20%" },
                        }}
                        inputProps={{ min: 1 }}
                      />
                    )}
                    <Button
                      onClick={handleAddLessonMetadataCategory}
                      startIcon={<AddIcon />}
                      variant="outlined"
                      disabled={!canAddLessonMetadataCategory}
                      sx={{
                        whiteSpace: "nowrap",
                        alignSelf: "flex-end",
                        flexShrink: 0,
                        flexBasis: { xs: "100%", sm: "20%" },
                        maxWidth: { xs: "100%", sm: "20%" },
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                  {lessonMetadataCategoryChips.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ flexWrap: "wrap" }}
                    >
                      {lessonMetadataCategoryChips.map(
                        ({ category, skilltype, questions, index }) => {
                          const numericQuestions =
                            normalizeNumericValue(questions);
                          const questionLabel =
                            isLessonMetadataAssessment &&
                            numericQuestions !== undefined &&
                            Number.isFinite(numericQuestions) &&
                            numericQuestions > 0
                              ? ` • ${numericQuestions} question${
                                  numericQuestions === 1 ? "" : "s"
                                }`
                              : "";

                          return (
                            <Chip
                              key={`lesson-metadata-chip-${category}-${skilltype}-${index}`}
                              label={`${category} • ${skilltype}${questionLabel}`}
                              onDelete={handleRemoveLessonMetadataCategory(
                                index
                              )}
                              variant="outlined"
                              color="primary"
                            />
                          );
                        }
                      )}
                    </Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={lessonMetadataForm.isAssignment}
                  onChange={handleLessonMetadataToggleChange("isAssignment")}
                />
              }
              label="Assignment for students"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={lessonMetadataForm.openForStudent}
                  onChange={handleLessonMetadataToggleChange("openForStudent")}
                />
              }
              label="Visible to students"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseLessonMetadataDialog}
            disabled={savingLessonMetadata}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveLessonMetadata}
            variant="contained"
            disabled={!lessonMetadataTarget || savingLessonMetadata}
          >
            {savingLessonMetadata ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lessonFlowOpen}
        onClose={handleCloseLessonFlow}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Create lesson</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Stepper activeStep={lessonFlowStep} alternativeLabel>
              {lessonFlowSteps.map((step, index) => (
                <Step key={step.key} completed={lessonFlowStep > index}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {lessonFlowError && (
              <Alert severity="error">{lessonFlowError}</Alert>
            )}
            {currentLessonFlowStepKey === "type" && (
              <Grid container spacing={2}>
                {LESSON_TYPE_OPTIONS.map((option, index) => {
                  const visuals = getLessonVisuals({
                    lessonType: option.value,
                  });
                  const Icon = visuals.icon;
                  const isSelected = lessonForm.lessonType === option.value;

                  return (
                    <Grow in timeout={200 + index * 60} key={option.value}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper
                          variant={isSelected ? "outlined" : "elevation"}
                          sx={{
                            height: "100%",
                            borderColor: isSelected
                              ? "primary.main"
                              : "transparent",
                            boxShadow: isSelected ? 6 : 1,
                          }}
                        >
                          <CardActionArea
                            onClick={() =>
                              handleLessonFormChange("lessonType")({
                                target: { value: option.value },
                              })
                            }
                          >
                            <Box sx={{ p: 2.5 }}>
                              <Stack spacing={1.5} alignItems="flex-start">
                                <Avatar
                                  variant="rounded"
                                  sx={(theme) => ({
                                    bgcolor:
                                      visuals.color === "default"
                                        ? theme.palette.grey[700]
                                        : theme.palette[visuals.color]?.main ||
                                          theme.palette.primary.main,
                                    color: theme.palette.common.white,
                                  })}
                                >
                                  <Icon fontSize="small" />
                                </Avatar>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {option.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Craft a {option.label.toLowerCase()}{" "}
                                  experience for your learners.
                                </Typography>
                              </Stack>
                            </Box>
                          </CardActionArea>
                        </Paper>
                      </Grid>
                    </Grow>
                  );
                })}
              </Grid>
            )}
            {currentLessonFlowStepKey === "details" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Lesson name"
                    value={lessonForm.name}
                    onChange={handleLessonFormChange("name")}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Lesson title"
                    value={lessonForm.title}
                    onChange={handleLessonFormChange("title")}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Duration (minutes)"
                    type="number"
                    value={lessonForm.duration}
                    onChange={handleLessonFormChange("duration")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Sequence"
                    value={lessonForm.seq}
                    onChange={handleLessonFormChange("seq")}
                    fullWidth
                  />
                </Grid>
                {isLessonFormAssessment && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Grades"
                      value={lessonForm.grades}
                      onChange={handleLessonFormChange("grades")}
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Description
                      </Typography>
                      {isLessonDescriptionEditing ? (
                        <Tooltip title="Done">
                          <IconButton
                            size="small"
                            onClick={() => setIsLessonDescriptionEditing(false)}
                            aria-label="Finish editing lesson description"
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={hasLessonDescription ? "Edit" : "Add"}>
                          <IconButton
                            size="small"
                            onClick={() => setIsLessonDescriptionEditing(true)}
                            aria-label="Edit lesson description"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Box
                      ref={lessonDescriptionContainerRef}
                      role={isLessonDescriptionEditing ? undefined : "textbox"}
                      tabIndex={isLessonDescriptionEditing ? -1 : 0}
                      aria-label="Lesson description"
                      onClick={() => {
                        if (!isLessonDescriptionEditing) {
                          setIsLessonDescriptionEditing(true);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (
                          isLessonDescriptionEditing ||
                          (event.key !== "Enter" && event.key !== " ")
                        ) {
                          return;
                        }

                        event.preventDefault();
                        setIsLessonDescriptionEditing(true);
                      }}
                      sx={(theme) => ({
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        border: isLessonDescriptionEditing
                          ? "none"
                          : `1px solid ${theme.palette.divider}`,
                        minHeight: 240,
                        cursor: isLessonDescriptionEditing ? "auto" : "text",
                        px: isLessonDescriptionEditing ? 0 : 2,
                        py: isLessonDescriptionEditing ? 0 : 1.5,
                        bgcolor: isLessonDescriptionEditing
                          ? "transparent"
                          : theme.palette.grey[50],
                        transition: "border-color 120ms ease-in-out",
                        "&:hover": !isLessonDescriptionEditing
                          ? { borderColor: theme.palette.text.secondary }
                          : undefined,
                        "& .tox-tinymce": { borderRadius: 8 },
                        "& .tox-edit-area iframe": { minHeight: 240 },
                      })}
                    >
                      {isLessonDescriptionEditing ? (
                        <LessonRichTextEditor
                          value={lessonForm.description}
                          onChange={handleLessonFormChange("description")}
                          minHeight={240}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            m: 0,
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            color: lessonForm.description
                              ? "text.primary"
                              : "text.disabled",
                          }}
                        >
                          {lessonForm.description ||
                            "Click to add a lesson description"}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {isLessonFormAssessment
                        ? "Categories, skill types & question totals"
                        : "Categories & skill types"}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", md: "flex-end" }}
                      sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
                    >
                      <TextField
                        label="Category"
                        value={lessonFormCategoryDraft.category}
                        onChange={handleLessonFormCategoryDraftChange(
                          "category"
                        )}
                        select
                        fullWidth
                        sx={{
                          flex: { xs: "1 1 100%", md: "0 0 320px" },
                          maxWidth: { md: 320 },
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            const normalizedValue =
                              normalizeCategoryValue(selected);

                            if (!normalizedValue) {
                              return (
                                <Typography color="text.disabled">
                                  Select Category
                                </Typography>
                              );
                            }

                            return getCategoryLabel(normalizedValue);
                          },
                        }}
                        helperText={taxonomyHelperText}
                        disabled={taxonomyLoading && !taxonomyOptions.length}
                      >
                        {(() => {
                          const categoryOptions = createCategoryOptions(
                            taxonomyOptions,
                            lessonFormCategoryDraft.category
                          );

                          if (categoryOptions.length) {
                            return categoryOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ));
                          }

                          return (
                            <MenuItem value="" disabled>
                              {taxonomyLoading
                                ? "Loading categories…"
                                : "No categories available"}
                            </MenuItem>
                          );
                        })()}
                      </TextField>
                      <TextField
                        label="Skill type"
                        value={lessonFormCategoryDraft.skilltype}
                        onChange={handleLessonFormCategoryDraftChange(
                          "skilltype"
                        )}
                        select
                        fullWidth
                        sx={{
                          width: { xs: "100%", md: 220 },
                          flex: { xs: "1 1 100%", md: "0 0 220px" },
                        }}
                      >
                        {createSkillOptions(
                          lessonFormCategoryDraft.skilltype
                        ).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {isLessonFormAssessment && (
                        <TextField
                          label="Questions"
                          type="number"
                          value={lessonFormCategoryDraft.questions}
                          onChange={handleLessonFormCategoryDraftChange(
                            "questions"
                          )}
                          fullWidth
                          sx={{
                            width: { xs: "100%", md: 160 },
                            flex: { xs: "1 1 100%", md: "0 0 160px" },
                          }}
                          inputProps={{ min: 1 }}
                        />
                      )}
                      <Button
                        onClick={handleAddLessonFormCategory}
                        startIcon={<AddIcon />}
                        variant="outlined"
                        disabled={!canAddLessonFormCategory}
                        sx={{
                          whiteSpace: "nowrap",
                          alignSelf: { xs: "stretch", md: "flex-end" },
                        }}
                      >
                        Add
                      </Button>
                    </Stack>
                    {lessonFormCategoryChips.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{
                          flexWrap: "wrap",
                        }}
                      >
                        {lessonFormCategoryChips.map(
                          ({ category, skilltype, questions, index }) => {
                            const numericQuestions =
                              normalizeNumericValue(questions);
                            const questionLabel =
                              isLessonFormAssessment &&
                              numericQuestions !== undefined &&
                              Number.isFinite(numericQuestions) &&
                              numericQuestions > 0
                                ? ` • ${numericQuestions} question${
                                    numericQuestions === 1 ? "" : "s"
                                  }`
                                : "";

                            return (
                              <Chip
                                key={`lesson-category-chip-${category}-${skilltype}-${index}`}
                                label={`${category} • ${skilltype}${questionLabel}`}
                                onDelete={handleRemoveLessonFormCategory(index)}
                                variant="outlined"
                                color="primary"
                              />
                            );
                          }
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={lessonForm.isAssignment}
                        onChange={handleLessonFormChange("isAssignment")}
                      />
                    }
                    label="Assignment for students"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={lessonForm.openForStudent}
                        onChange={handleLessonFormChange("openForStudent")}
                      />
                    }
                    label="Visible to students"
                  />
                </Grid>
              </Grid>
            )}
            {currentLessonFlowStepKey === "content" && (
              <Stack spacing={2}>
                <TextField
                  label="Content name"
                  value={newLessonContent.Name}
                  onChange={handleLessonContentDraftChange("Name")}
                  fullWidth
                />
                <TextField
                  label="Content title"
                  value={newLessonContent.Title}
                  onChange={handleLessonContentDraftChange("Title")}
                  fullWidth
                />
                {lessonForm.isAssignment ? (
                  <AssignmentAuthoringShell
                    initialSections={newLessonContent.sections || []}
                  />
                ) : (
                  <Box
                    sx={{
                      "& .tox-tinymce": { borderRadius: 2 },
                      "& .tox-edit-area iframe": { minHeight: 600 },
                    }}
                  >
                    <LessonRichTextEditor
                      value={newLessonContent.Value}
                      onChange={handleLessonContentDraftChange("Value")}
                      minHeight={600}
                    />
                  </Box>
                )}
              </Stack>
            )}
            {currentLessonFlowStepKey === "review" && (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600 }}
                    gutterBottom
                  >
                    Lesson summary
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      Name: {lessonForm.name}
                    </Typography>
                    <Typography variant="body2">
                      Title: {lessonForm.title}
                    </Typography>
                    <Typography variant="body2">
                      Type:{" "}
                      {(() => {
                        const match = matchLessonTypeOption(
                          lessonForm.lessonType
                        );
                        return match
                          ? match.label
                          : LESSON_TYPE_PLACEHOLDER.label;
                      })()}
                    </Typography>
                    <Typography variant="body2">
                      Description:{" "}
                      {stripHtmlTags(lessonForm.description) || "—"}
                    </Typography>
                    {isLessonFormAssessment && (
                      <Typography variant="body2">
                        Grades: {lessonForm.grades || "—"}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {isLessonFormAssessment
                        ? "Assessment settings"
                        : "Categories"}
                      :{" "}
                      {(() => {
                        if (isLessonFormAssessment) {
                          const assessmentEntries = normalizeAssessmentEntries(
                            lessonForm.categories
                          );

                          if (!assessmentEntries.length) {
                            return "—";
                          }

                          const validEntries = assessmentEntries.filter(
                            (entry) =>
                              entry.category &&
                              entry.skilltype &&
                              entry.numericQuestions !== undefined &&
                              Number.isFinite(entry.numericQuestions) &&
                              entry.numericQuestions > 0
                          );

                          if (!validEntries.length) {
                            return "—";
                          }

                          return validEntries
                            .map(
                              (entry) =>
                                `${entry.category} (${entry.skilltype}) – ${
                                  entry.numericQuestions
                                } question${
                                  entry.numericQuestions === 1 ? "" : "s"
                                }`
                            )
                            .join(", ");
                        }

                        const summaryCategories = formatCategoriesForPayload(
                          lessonForm.categories
                        );
                        if (!summaryCategories.length) {
                          return "—";
                        }

                        return summaryCategories
                          .map(
                            (entry) => `${entry.category} (${entry.skilltype})`
                          )
                          .join(", ");
                      })()}
                    </Typography>
                    <Typography variant="body2">
                      Duration: {lessonForm.duration || "—"} minutes
                    </Typography>
                    <Typography variant="body2">
                      Assignment: {lessonForm.isAssignment ? "Yes" : "No"}
                    </Typography>
                    <Typography variant="body2">
                      Visibility:{" "}
                      {lessonForm.openForStudent
                        ? "Visible to students"
                        : "Hidden"}
                    </Typography>
                  </Stack>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    maxHeight: 280,
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600 }}
                    gutterBottom
                  >
                    Preview
                  </Typography>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html:
                        newLessonContent.Value || "<p>No content yet.</p>",
                    }}
                  />
                </Paper>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={
              lessonFlowStep === 0
                ? handleCloseLessonFlow
                : handleBackLessonFlowStep
            }
            disabled={lessonFlowSaving}
          >
            {lessonFlowStep === 0 ? "Cancel" : "Back"}
          </Button>
          <Button
            onClick={
              lessonFlowStep === lessonFlowSteps.length - 1
                ? handleSubmitLessonFlow
                : handleNextLessonFlowStep
            }
            variant="contained"
            disabled={lessonFlowSaving}
          >
            {lessonFlowStep === lessonFlowSteps.length - 1
              ? lessonFlowSaving
                ? "Saving..."
                : "Create lesson"
              : "Next"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default LessonsManager;
